import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BadgeCheck,
  Boxes,
  Check,
  Code2,
  Copy,
  Download,
  ExternalLink,
  FileCode2,
  Gauge,
  Info,
  LoaderCircle,
  LogIn,
  LogOut,
  Mail,
  Palette,
  RefreshCw,
  Trash2,
  User,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Brand } from './components/Brand';
import { apiUrl, appConfig } from './lib/config';

type Feature = {
  icon: LucideIcon;
  title: string;
  copy: string;
  tone: 'blue' | 'green' | 'coral' | 'gold';
};

type WorkflowStep = {
  index: string;
  title: string;
  copy: string;
};

type ModelRow = {
  id: string;
  owner: string;
  inputCostPerMillionTokens: number | null;
  inputUsesOriginalPrice: boolean;
  outputCostPerMillionTokens: number | null;
  outputUsesOriginalPrice: boolean;
};

type SortKey = 'input' | 'output';
type SortDirection = 'asc' | 'desc';
type AuthMode = 'login' | 'register';

type AuthFormState = {
  confirmPassword: string;
  email: string;
  password: string;
};

type AuthSession = {
  accessToken: string;
  backendBaseUrl: string;
  email: string;
  expiresAt: string;
  userId: number | string | null;
};

type AuthPayload = {
  accessToken?: unknown;
  email?: unknown;
  expiresAt?: unknown;
  sessionExpiresAt?: unknown;
  userId?: unknown;
  user_id?: unknown;
};

type WalletBalance = {
  availableMilliCents: number;
  balanceMilliCents: number;
  reservedMilliCents: number;
};

type UsageBucket = {
  bucket: string;
  completionTokens: number;
  costMilliCents: number;
  promptTokens: number;
  requestCount: number;
  totalTokens: number;
};

type UsageActivityRecord = {
  completionTokens: number;
  costMilliCents: number;
  createdAt: number | string;
  endpoint: string;
  id: string;
  model: string;
  promptTokens: number;
  status: string;
  totalTokens: number;
};

type UsageActivityPage = {
  hasMore: boolean;
  items: UsageActivityRecord[];
  nextCursor: string | null;
};

type RechargeRecord = {
  amountMilliCents: number;
  availableAfterMilliCents: number;
  balanceAfterMilliCents: number;
  createdAt: number | string;
  externalId: string;
  id: number | string;
  memo: string;
  metadata: Record<string, unknown>;
};

type RechargeRecordPage = {
  hasMore: boolean;
  items: RechargeRecord[];
  nextCursor: string | null;
};

type AccountUsageData = {
  buckets: UsageBucket[];
  wallet: WalletBalance;
};

type ShareKeyRecord = {
  createdAt: number | string | null;
  expiresAt: number | string | null;
  id: number | string;
  key?: string;
  keyPreview: string;
  lastUsedAt: number | string | null;
  title: string;
};

type ShareKeyFormState = {
  expiresAt: string;
  neverExpires: boolean;
  title: string;
};

type RechargeExchangeRate = {
  baseRate: number;
  chargedRate: number;
  source: string;
  updatedAt: string;
};

type AlipayPaymentOrder = {
  amountMilliCents: number;
  exchangeRate: number | null;
  outTradeNo: string;
  paymentUrl?: string;
  status: string;
  totalAmountCny: number;
  tradeNo?: string | null;
  tradeStatus?: string | null;
  wallet?: WalletBalance;
};

type UsageFilters = {
  from: string;
  to: string;
};

type UsageChartMode = 'cost' | 'requests';

type RawModel = {
  id?: unknown;
  name?: unknown;
  owned_by?: unknown;
  owner?: unknown;
  provider?: unknown;
  inputCostPerMillionTokens?: unknown;
  outputCostPerMillionTokens?: unknown;
  input_cost_per_million_tokens?: unknown;
  output_cost_per_million_tokens?: unknown;
  input_price_per_million_tokens?: unknown;
  output_price_per_million_tokens?: unknown;
  pricing?: {
    prompt?: unknown;
    completion?: unknown;
    input?: unknown;
    output?: unknown;
    inputPerMillionTokens?: unknown;
    outputPerMillionTokens?: unknown;
    pricingSource?: {
      prompt?: unknown;
      completion?: unknown;
    };
  };
};

const features: Feature[] = [
  {
    icon: Palette,
    title: '设计生成',
    copy: '从一句产品想法开始，生成页面方向、视觉系统、关键组件和可继续迭代的方案。',
    tone: 'blue',
  },
  {
    icon: Code2,
    title: '代码交付',
    copy: '把选中的方案推进到真实前端代码、资源包和交付清单，减少从视觉到开发的断层。',
    tone: 'green',
  },
  {
    icon: Boxes,
    title: '资产管理',
    copy: '页面、插画、图标、提示词和设计规范归档到同一项目，方便复用和追踪。',
    tone: 'coral',
  },
  {
    icon: Gauge,
    title: '用量透明',
    copy: '模型调用、项目成本和账单记录可以对齐到后端账本，为团队付费和审计留出空间。',
    tone: 'gold',
  },
];

const workflow: WorkflowStep[] = [
  { index: '01', title: '描述想法', copy: '输入产品目标、目标用户、页面范围和偏好的视觉方向。' },
  { index: '02', title: '生成方案', copy: '由 Agent 产出页面结构、视觉方向、资产建议和可选版本。' },
  { index: '03', title: '确认风格', copy: '保留喜欢的方向，继续细化组件、页面状态和品牌细节。' },
  { index: '04', title: '落地代码', copy: '输出可预览、可打包、可继续开发的前端项目。' },
];

const fallbackModels: ModelRow[] = [
  {
    id: 'gpt-5.4',
    owner: 'Vibeco',
    inputCostPerMillionTokens: null,
    inputUsesOriginalPrice: false,
    outputCostPerMillionTokens: null,
    outputUsesOriginalPrice: false,
  },
  {
    id: 'gpt-image-1.5',
    owner: 'Vibeco',
    inputCostPerMillionTokens: null,
    inputUsesOriginalPrice: false,
    outputCostPerMillionTokens: null,
    outputUsesOriginalPrice: false,
  },
];

const AUTH_SESSION_KEY = 'vibeco.frontend.authSession';
const RECHARGE_RATE_MARKUP = 1.005;
const RECHARGE_EXCHANGE_RATE_ENDPOINTS = [
  {
    label: 'Currency API / jsDelivr',
    url: 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
  },
  {
    label: 'ExchangeRate-API',
    url: 'https://open.er-api.com/v6/latest/USD',
  },
  {
    label: 'ExchangeRate-API v4',
    url: 'https://api.exchangerate-api.com/v4/latest/USD',
  },
];
const EXCHANGE_RATE_TIMEOUT_MS = 6000;

function readText(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function readCost(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const normalized = Number(value.replace(/[¥,$]/g, '').trim());
      if (Number.isFinite(normalized)) return normalized;
    }
  }
  return null;
}

function readPerTokenCost(...values: unknown[]) {
  const cost = readCost(...values);
  return cost === null ? null : cost * 1_000_000;
}

function usesOriginalPrice(value: unknown) {
  return typeof value === 'string' && value.trim().toLowerCase() === 'original';
}

function normalizeModels(payload: unknown): ModelRow[] {
  const rawModels = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown })?.data)
      ? (payload as { data: unknown[] }).data
      : [];

  return rawModels
    .map((item): ModelRow | null => {
      const model = item as RawModel;
      const id = readText(model.id, readText(model.name, 'unknown-model'));
      if (id === 'unknown-model') return null;

      return {
        id,
        owner: readText(model.owned_by, readText(model.owner, readText(model.provider, 'Vibeco'))),
        inputCostPerMillionTokens: readCost(
          model.inputCostPerMillionTokens,
          model.input_cost_per_million_tokens,
          model.input_price_per_million_tokens,
          model.pricing?.inputPerMillionTokens,
        ) ?? readPerTokenCost(model.pricing?.prompt, model.pricing?.input),
        inputUsesOriginalPrice: usesOriginalPrice(model.pricing?.pricingSource?.prompt),
        outputCostPerMillionTokens: readCost(
          model.outputCostPerMillionTokens,
          model.output_cost_per_million_tokens,
          model.output_price_per_million_tokens,
          model.pricing?.outputPerMillionTokens,
        ) ?? readPerTokenCost(model.pricing?.completion, model.pricing?.output),
        outputUsesOriginalPrice: usesOriginalPrice(model.pricing?.pricingSource?.completion),
      };
    })
    .filter((model): model is ModelRow => Boolean(model));
}

function formatCost(value: number | null) {
  if (value === null) return '待配置';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function ModelPrice({ isOriginal, value }: { isOriginal: boolean; value: number | null }) {
  return (
    <span className="model-price-value">
      <span>{formatCost(value)}</span>
      {isOriginal ? <span className="model-price-tag">官方原价</span> : null}
    </span>
  );
}

function compareCosts(first: number | null, second: number | null, direction: SortDirection) {
  if (first === null && second === null) return 0;
  if (first === null) return 1;
  if (second === null) return -1;
  return direction === 'asc' ? first - second : second - first;
}

function readNumber(value: unknown, fallback = 0) {
  const number = readCost(value);
  return number === null ? fallback : number;
}

function formatInteger(value: number) {
  return value.toLocaleString('en-US');
}

function formatMilliCents(value: number) {
  return `$${(value / 100_000).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function formatCompactMilliCents(value: number) {
  return `$${(value / 100_000).toLocaleString('en-US', { maximumFractionDigits: 4, minimumFractionDigits: value > 0 && value < 1_000 ? 4 : 2 })}`;
}

function formatCnyAmount(value: number) {
  return `¥${value.toLocaleString('zh-CN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function formatCnyPayloadAmount(value: number) {
  return value.toFixed(2);
}

function formatExchangeRatePayload(value: number) {
  return value.toFixed(6);
}

function formatCnyRate(value: number) {
  return `¥${value.toLocaleString('zh-CN', { maximumFractionDigits: 4, minimumFractionDigits: 4 })}`;
}

function formatUsdAmount(value: number) {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function parseRechargeUsdAmount(value: string) {
  const amount = Number(value.replace(/,/g, '').trim());
  return Number.isFinite(amount) ? amount : 0;
}

function parseRechargeMilliCents(value: string) {
  const amount = parseRechargeUsdAmount(value);
  return amount > 0 ? Math.round(amount * 100_000) : 0;
}

function readAlipayReturnOutTradeNo() {
  const params = new URLSearchParams(window.location.search);
  const payment = params.get('payment');
  const outTradeNo = params.get('outTradeNo') || params.get('out_trade_no') || '';
  return payment === 'alipay' && outTradeNo ? outTradeNo : '';
}

function clearAlipayReturnParams() {
  const target = `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(null, '', target);
}

function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateTimeLocal(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function parseDateInput(value: string) {
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function defaultUsageRange() {
  const today = new Date();
  return {
    from: formatDateInput(addDays(today, -6)),
    to: formatDateInput(today),
  };
}

function isValidUsageRange(from: string, to: string) {
  const fromDate = parseDateInput(from);
  const toDate = parseDateInput(to);
  return Boolean(fromDate && toDate && fromDate.getTime() <= toDate.getTime());
}

function formatChartDate(value: string) {
  const date = parseDateInput(value);
  if (!date) return value;
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function normalizeTimestampValue(value: number | string) {
  if (typeof value === 'number') return value > 0 && value < 1_000_000_000_000 ? value * 1000 : value;
  const parsedNumber = Number(value);
  if (Number.isFinite(parsedNumber)) return parsedNumber > 0 && parsedNumber < 1_000_000_000_000 ? parsedNumber * 1000 : parsedNumber;
  return value;
}

function formatActivityTimestamp(value: number | string) {
  const normalizedValue = normalizeTimestampValue(value);
  const date = new Date(normalizedValue);
  if (!Number.isFinite(date.getTime())) return { date: value || '-', time: '' };
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    date: date.toLocaleDateString('zh-CN', { day: 'numeric', month: 'long', timeZone, year: 'numeric' }),
    time: date.toLocaleTimeString('zh-CN', { hour: '2-digit', hour12: false, minute: '2-digit', timeZone }),
  };
}

function formatShareKeyExpiry(value: number | string | null) {
  if (value === null || value === '') return '永不过期';
  const timestamp = formatActivityTimestamp(value);
  return timestamp.time ? `${timestamp.date} ${timestamp.time}` : timestamp.date;
}

function defaultShareKeyForm(): ShareKeyFormState {
  return {
    expiresAt: formatDateTimeLocal(addDays(new Date(), 30)),
    neverExpires: true,
    title: '',
  };
}

function normalizeWalletBalance(payload: unknown): WalletBalance {
  const wallet = payload as {
    availableMilliCents?: unknown;
    available_milli_cents?: unknown;
    balanceMilliCents?: unknown;
    balance_milli_cents?: unknown;
    reservedMilliCents?: unknown;
    reserved_milli_cents?: unknown;
  };

  return {
    availableMilliCents: readNumber(wallet.availableMilliCents ?? wallet.available_milli_cents),
    balanceMilliCents: readNumber(wallet.balanceMilliCents ?? wallet.balance_milli_cents),
    reservedMilliCents: readNumber(wallet.reservedMilliCents ?? wallet.reserved_milli_cents),
  };
}

function normalizeAlipayPaymentOrder(payload: unknown): AlipayPaymentOrder {
  const order = payload as {
    amountMilliCents?: unknown;
    amount_milli_cents?: unknown;
    exchangeRate?: unknown;
    exchange_rate?: unknown;
    outTradeNo?: unknown;
    out_trade_no?: unknown;
    paymentUrl?: unknown;
    payment_url?: unknown;
    status?: unknown;
    totalAmount?: unknown;
    totalAmountCny?: unknown;
    total_amount?: unknown;
    tradeNo?: unknown;
    trade_no?: unknown;
    tradeStatus?: unknown;
    trade_status?: unknown;
    wallet?: unknown;
  };
  return {
    amountMilliCents: readNumber(order.amountMilliCents ?? order.amount_milli_cents),
    exchangeRate: readCost(order.exchangeRate ?? order.exchange_rate),
    outTradeNo: readText(order.outTradeNo ?? order.out_trade_no, ''),
    paymentUrl: readText(order.paymentUrl ?? order.payment_url, ''),
    status: readText(order.status, 'pending'),
    totalAmountCny: readNumber(order.totalAmountCny ?? order.totalAmount ?? order.total_amount),
    tradeNo: readText(order.tradeNo ?? order.trade_no, ''),
    tradeStatus: readText(order.tradeStatus ?? order.trade_status, ''),
    wallet: order.wallet ? normalizeWalletBalance(order.wallet) : undefined,
  };
}

function normalizeAlipayPaymentOrders(payload: unknown) {
  const items = Array.isArray((payload as { items?: unknown })?.items) ? (payload as { items: unknown[] }).items : [];
  return items
    .map((item) => normalizeAlipayPaymentOrder(item))
    .filter((order) => Boolean(order.outTradeNo));
}

function normalizeUsageBuckets(payload: unknown): UsageBucket[] {
  const items = Array.isArray((payload as { items?: unknown })?.items) ? (payload as { items: unknown[] }).items : [];
  return items.map((item) => {
    const bucket = item as {
      bucket?: unknown;
      completionTokens?: unknown;
      costMilliCents?: unknown;
      promptTokens?: unknown;
      requestCount?: unknown;
      totalTokens?: unknown;
    };

    return {
      bucket: readText(bucket.bucket, '-'),
      completionTokens: readNumber(bucket.completionTokens),
      costMilliCents: readNumber(bucket.costMilliCents),
      promptTokens: readNumber(bucket.promptTokens),
      requestCount: readNumber(bucket.requestCount),
      totalTokens: readNumber(bucket.totalTokens),
    };
  });
}

function normalizeRechargeRecord(payload: unknown): RechargeRecord {
  const record = payload as {
    amountMilliCents?: unknown;
    amount_milli_cents?: unknown;
    availableAfterMilliCents?: unknown;
    available_after_milli_cents?: unknown;
    balanceAfterMilliCents?: unknown;
    balance_after_milli_cents?: unknown;
    createdAt?: unknown;
    created_at?: unknown;
    externalId?: unknown;
    external_id?: unknown;
    id?: unknown;
    memo?: unknown;
    metadata?: unknown;
  };
  const metadata = record.metadata && typeof record.metadata === 'object' && !Array.isArray(record.metadata)
    ? record.metadata as Record<string, unknown>
    : {};

  return {
    amountMilliCents: readNumber(record.amountMilliCents ?? record.amount_milli_cents),
    availableAfterMilliCents: readNumber(record.availableAfterMilliCents ?? record.available_after_milli_cents),
    balanceAfterMilliCents: readNumber(record.balanceAfterMilliCents ?? record.balance_after_milli_cents),
    createdAt: readText(record.createdAt ?? record.created_at, ''),
    externalId: readText(record.externalId ?? record.external_id, ''),
    id: readText(record.id, '') || readNumber(record.id),
    memo: readText(record.memo, ''),
    metadata,
  };
}

function normalizeRechargeRecordPage(payload: unknown): RechargeRecordPage {
  const page = payload as {
    hasMore?: unknown;
    has_more?: unknown;
    items?: unknown;
    nextCursor?: unknown;
    next_cursor?: unknown;
  };
  const items = Array.isArray(page.items) ? page.items : [];
  const nextCursorValue = page.nextCursor ?? page.next_cursor;
  const nextCursor = nextCursorValue === null || nextCursorValue === undefined || nextCursorValue === ''
    ? null
    : String(nextCursorValue);

  return {
    hasMore: Boolean(page.hasMore ?? page.has_more),
    items: items.map((item) => normalizeRechargeRecord(item)).filter((item) => item.id !== ''),
    nextCursor,
  };
}

function normalizeUsageActivityPage(payload: unknown): UsageActivityPage {
  const result = payload as { hasMore?: unknown; items?: unknown; nextCursor?: unknown };
  const items = Array.isArray(result.items) ? result.items : [];

  return {
    hasMore: result.hasMore === true,
    items: items.map((item) => {
      const record = item as {
        completionTokens?: unknown;
        costMilliCents?: unknown;
        createdAt?: unknown;
        endpoint?: unknown;
        id?: unknown;
        model?: unknown;
        promptTokens?: unknown;
        requestId?: unknown;
        status?: unknown;
        totalTokens?: unknown;
      };

      return {
        completionTokens: readNumber(record.completionTokens),
        costMilliCents: readNumber(record.costMilliCents),
        createdAt: typeof record.createdAt === 'number' ? record.createdAt : readText(record.createdAt, ''),
        endpoint: readText(record.endpoint, 'API: vibeco'),
        id: readText(record.id, readText(record.requestId, crypto.randomUUID())),
        model: readText(record.model, '-'),
        promptTokens: readNumber(record.promptTokens),
        status: readText(record.status, '-'),
        totalTokens: readNumber(record.totalTokens),
      };
    }),
    nextCursor: readText(result.nextCursor, '') || null,
  };
}

function readTimestampField(value: unknown): number | string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function readRecordId(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return readText(value, crypto.randomUUID());
}

function copyTextWithFallback(text: string) {
  const copyTarget = document.createElement('textarea');
  copyTarget.value = text;
  copyTarget.setAttribute('readonly', '');
  copyTarget.style.position = 'fixed';
  copyTarget.style.left = '-9999px';
  document.body.appendChild(copyTarget);
  copyTarget.select();
  document.execCommand('copy');
  document.body.removeChild(copyTarget);
}

async function copyTextToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      copyTextWithFallback(text);
      return;
    }
  }
  copyTextWithFallback(text);
}

function normalizeShareKey(item: unknown): ShareKeyRecord {
  const record = item as {
    createdAt?: unknown;
    created_at?: unknown;
    expiresAt?: unknown;
    expires_at?: unknown;
    id?: unknown;
    key?: unknown;
    keyPreview?: unknown;
    key_preview?: unknown;
    lastUsedAt?: unknown;
    last_used_at?: unknown;
    title?: unknown;
    tokenPreview?: unknown;
    token_preview?: unknown;
  };

  return {
    createdAt: readTimestampField(record.createdAt ?? record.created_at),
    expiresAt: readTimestampField(record.expiresAt ?? record.expires_at),
    id: readRecordId(record.id),
    key: readText(record.key, ''),
    keyPreview: readText(record.keyPreview ?? record.key_preview ?? record.tokenPreview ?? record.token_preview, ''),
    lastUsedAt: readTimestampField(record.lastUsedAt ?? record.last_used_at),
    title: readText(record.title, '未命名 Key'),
  };
}

function normalizeShareKeys(payload: unknown): ShareKeyRecord[] {
  const items = Array.isArray((payload as { items?: unknown })?.items) ? (payload as { items: unknown[] }).items : [];
  return items.map(normalizeShareKey);
}

function fillUsageBuckets(from: string, to: string, buckets: UsageBucket[]) {
  const fromDate = parseDateInput(from);
  const toDate = parseDateInput(to);
  if (!fromDate || !toDate || fromDate.getTime() > toDate.getTime()) return buckets;

  const bucketMap = new Map(buckets.map((bucket) => [bucket.bucket, bucket]));
  const filledBuckets: UsageBucket[] = [];
  for (let date = fromDate; date.getTime() <= toDate.getTime(); date = addDays(date, 1)) {
    const bucketKey = formatDateInput(date);
    filledBuckets.push(bucketMap.get(bucketKey) ?? {
      bucket: bucketKey,
      completionTokens: 0,
      costMilliCents: 0,
      promptTokens: 0,
      requestCount: 0,
      totalTokens: 0,
    });
  }
  return filledBuckets;
}

function sumUsageBuckets(buckets: UsageBucket[]) {
  return buckets.reduce(
    (total, bucket) => ({
      completionTokens: total.completionTokens + bucket.completionTokens,
      costMilliCents: total.costMilliCents + bucket.costMilliCents,
      promptTokens: total.promptTokens + bucket.promptTokens,
      requestCount: total.requestCount + bucket.requestCount,
      totalTokens: total.totalTokens + bucket.totalTokens,
    }),
    { completionTokens: 0, costMilliCents: 0, promptTokens: 0, requestCount: 0, totalTokens: 0 },
  );
}

function authenticatedApiUrl(session: AuthSession, path: string) {
  const backendBaseUrl = session.backendBaseUrl || appConfig.apiBaseUrl;
  if (!backendBaseUrl) throw new Error('账号服务不可用');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${backendBaseUrl.replace(/\/$/, '')}${normalizedPath}`;
}

function readSessionExpiresAt(payload: unknown, headers: Headers) {
  return readText((payload as { sessionExpiresAt?: unknown })?.sessionExpiresAt, headers.get('x-vibeco-session-expires-at') || '');
}

function refreshSessionExpiresAt(session: AuthSession, expiresAt: string) {
  if (!expiresAt || !Number.isFinite(Date.parse(expiresAt)) || expiresAt === session.expiresAt) return session;
  return { ...session, expiresAt };
}

function authSessionFromPayload(payload: AuthPayload, backendBaseUrl: string): AuthSession {
  const accessToken = readText(payload.accessToken, '');
  const expiresAt = readText(payload.expiresAt, readText(payload.sessionExpiresAt, ''));
  const email = readText(payload.email, '');

  if (!accessToken || !expiresAt || !Number.isFinite(Date.parse(expiresAt))) {
    throw new Error('登录响应不完整，请稍后重试。');
  }

  return {
    accessToken,
    backendBaseUrl,
    email,
    expiresAt,
    userId: (payload.userId ?? payload.user_id ?? null) as number | string | null,
  };
}

function readStoredAuthSession() {
  try {
    const rawSession = window.localStorage.getItem(AUTH_SESSION_KEY);
    if (!rawSession) return null;
    const session = JSON.parse(rawSession) as AuthSession;
    if (!session.accessToken || !session.expiresAt || Date.parse(session.expiresAt) <= Date.now()) {
      window.localStorage.removeItem(AUTH_SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    window.localStorage.removeItem(AUTH_SESSION_KEY);
    return null;
  }
}

function writeStoredAuthSession(session: AuthSession) {
  window.localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
}

function clearStoredAuthSession() {
  window.localStorage.removeItem(AUTH_SESSION_KEY);
}

function sanitizeReturnTo(value: string | null) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
  const pathOnly = value.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return pathOnly === '/login' ? '/' : value;
}

function currentReturnToPath() {
  return sanitizeReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`);
}

function returnToFromLocation() {
  return sanitizeReturnTo(new URLSearchParams(window.location.search).get('returnTo'));
}

function loginHrefForCurrentPage(isLoginPage: boolean) {
  if (isLoginPage) return '/login';
  return `/login?returnTo=${encodeURIComponent(currentReturnToPath())}`;
}

async function authRequest(mode: AuthMode, form: AuthFormState) {
  const email = form.email.trim().toLowerCase();
  const password = form.password;

  if (!email) throw new Error('请输入邮箱。');
  if (!password) throw new Error('请输入密码。');
  if (mode === 'register' && password !== form.confirmPassword) {
    throw new Error('两次输入的密码不一致');
  }

  const routePath = mode === 'register' ? '/users/register' : '/auth/login';
  const endpoint = apiUrl(routePath);
  if (!endpoint) throw new Error('账号服务不可用');

  const response = await fetch(endpoint, {
    body: JSON.stringify({ email, password }),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new Error(payload?.error?.message || payload?.message || `云端服务请求失败：${response.status}`);
  }

  return authSessionFromPayload(payload, appConfig.apiBaseUrl);
}

async function authenticatedJsonRequest(session: AuthSession, path: string, options: RequestInit = {}) {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  headers.set('Authorization', `Bearer ${session.accessToken}`);
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(authenticatedApiUrl(session, path), {
    ...options,
    headers: {
      ...Object.fromEntries(headers.entries()),
    },
  });
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const message = response.status === 401
      ? '登录已过期，请重新登录。'
      : payload?.error?.message || payload?.message || `云端服务请求失败：${response.status}`;
    throw new Error(message);
  }

  return {
    payload,
    sessionExpiresAt: readSessionExpiresAt(payload, response.headers),
  };
}

async function shareKeysRequest(session: AuthSession) {
  const result = await authenticatedJsonRequest(session, '/share-keys');
  return {
    items: normalizeShareKeys(result.payload),
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
  };
}

async function createShareKeyRequest(session: AuthSession, form: ShareKeyFormState) {
  const title = form.title.trim();
  if (!title) throw new Error('请输入标题。');
  const expiresAt = form.neverExpires ? null : new Date(form.expiresAt);
  if (!form.neverExpires && (!expiresAt || !Number.isFinite(expiresAt.getTime()))) {
    throw new Error('请选择有效的到期时间。');
  }

  const result = await authenticatedJsonRequest(session, '/share-keys', {
    body: JSON.stringify({
      expiresAt: form.neverExpires ? null : expiresAt?.toISOString(),
      neverExpires: form.neverExpires,
      title,
    }),
    method: 'POST',
  });
  return {
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
    shareKey: normalizeShareKey(result.payload),
  };
}

async function deleteShareKeyRequest(session: AuthSession, id: number | string) {
  const result = await authenticatedJsonRequest(session, `/share-keys/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
  });
  return refreshSessionExpiresAt(session, result.sessionExpiresAt);
}

function usageQueryString(filters: UsageFilters, extra: Record<string, string> = {}) {
  const params = new URLSearchParams(extra);
  params.set('from', filters.from);
  params.set('to', filters.to);
  return params.toString();
}

async function walletAndUsageSummaryRequest(session: AuthSession, filters: UsageFilters) {
  const [walletResult, usageResult] = await Promise.all([
    authenticatedJsonRequest(session, '/wallet/balance'),
    authenticatedJsonRequest(session, `/usage/activity/summary?${usageQueryString(filters, { groupBy: 'day' })}`),
  ]);
  let refreshedSession = refreshSessionExpiresAt(session, walletResult.sessionExpiresAt);
  refreshedSession = refreshSessionExpiresAt(refreshedSession, usageResult.sessionExpiresAt);

  return {
    buckets: normalizeUsageBuckets(usageResult.payload),
    refreshedSession,
    wallet: normalizeWalletBalance(walletResult.payload),
  };
}

async function usageActivityRequest(session: AuthSession, filters: UsageFilters, before: string) {
  const query = usageQueryString(filters, { limit: '10', ...(before ? { before } : {}) });
  const result = await authenticatedJsonRequest(session, `/usage/activity?${query}`);
  return {
    page: normalizeUsageActivityPage(result.payload),
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
  };
}

async function rechargeRecordsRequest(session: AuthSession, before: string) {
  const params = new URLSearchParams({ limit: '5' });
  if (before) params.set('before', before);
  const result = await authenticatedJsonRequest(session, `/wallet/recharges?${params.toString()}`);
  return {
    page: normalizeRechargeRecordPage(result.payload),
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
  };
}

async function createAlipayPaymentRequest(session: AuthSession, amountMilliCents: number, totalAmountCny: string, exchangeRate: string) {
  const result = await authenticatedJsonRequest(session, '/payments/alipay/page-pay', {
    body: JSON.stringify({
      amountMilliCents,
      exchangeRate,
      returnUrl: `${window.location.origin}/usage`,
      totalAmountCny,
    }),
    method: 'POST',
  });
  return {
    order: normalizeAlipayPaymentOrder(result.payload),
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
  };
}

function normalizeUsdCnyRate(payload: unknown) {
  const record = payload as {
    date?: unknown;
    rates?: { CNY?: unknown; cny?: unknown };
    time_last_update_utc?: unknown;
    usd?: { cny?: unknown; CNY?: unknown };
  };
  const baseRate = readCost(record.usd?.cny, record.usd?.CNY, record.rates?.CNY, record.rates?.cny);
  if (baseRate === null || baseRate <= 0) return null;

  return {
    baseRate,
    chargedRate: baseRate * RECHARGE_RATE_MARKUP,
    updatedAt: readText(record.time_last_update_utc, readText(record.date, '')),
  };
}

async function fetchJsonWithTimeout(url: string) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), EXCHANGE_RATE_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`汇率接口返回 ${response.status}`);
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
  }
}

async function fetchUsdCnyExchangeRate(): Promise<RechargeExchangeRate> {
  for (const endpoint of RECHARGE_EXCHANGE_RATE_ENDPOINTS) {
    try {
      const payload = await fetchJsonWithTimeout(endpoint.url);
      const rate = normalizeUsdCnyRate(payload);
      if (!rate) throw new Error('汇率响应缺少 USD/CNY');
      return {
        ...rate,
        source: endpoint.label,
      };
    } catch {
      // Try the next public endpoint; browser CORS and regional availability can differ.
    }
  }

  throw new Error('公开汇率服务暂不可用，请稍后重试。');
}

async function syncAlipayPaymentRequest(session: AuthSession, outTradeNo: string) {
  const result = await authenticatedJsonRequest(session, `/payments/alipay/orders/${encodeURIComponent(outTradeNo)}`);
  return {
    order: normalizeAlipayPaymentOrder(result.payload),
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
  };
}

async function syncPendingAlipayPaymentsRequest(session: AuthSession) {
  const result = await authenticatedJsonRequest(session, '/payments/alipay/pending-orders?limit=8');
  const payload = result.payload as {
    paidCount?: unknown;
    pendingCount?: unknown;
    wallet?: unknown;
  };
  return {
    orders: normalizeAlipayPaymentOrders(result.payload),
    paidCount: readNumber(payload.paidCount),
    pendingCount: readNumber(payload.pendingCount),
    refreshedSession: refreshSessionExpiresAt(session, result.sessionExpiresAt),
    wallet: payload.wallet ? normalizeWalletBalance(payload.wallet) : undefined,
  };
}

async function logoutRequest(session: AuthSession) {
  const backendBaseUrl = session.backendBaseUrl || appConfig.apiBaseUrl;
  if (!backendBaseUrl || !session.accessToken) return;

  await fetch(`${backendBaseUrl.replace(/\/$/, '')}/auth/logout`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
    },
    method: 'POST',
  });
}

type UserMenuProps = {
  loginHref: string;
  onLogout: () => void | Promise<void>;
  session: AuthSession | null;
};

function UserMenu({ loginHref, onLogout, session }: UserMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmingLogout, setIsConfirmingLogout] = useState(false);
  const isAuthenticated = Boolean(session);

  useEffect(() => {
    if (!isAuthenticated) setIsConfirmingLogout(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isConfirmingLogout) return undefined;

    function closeDialog(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsConfirmingLogout(false);
    }

    window.addEventListener('keydown', closeDialog);
    return () => window.removeEventListener('keydown', closeDialog);
  }, [isConfirmingLogout]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function closeMenuOnOutsidePointer(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && !menuRef.current?.contains(target)) {
        setIsOpen(false);
      }
    }

    window.addEventListener('pointerdown', closeMenuOnOutsidePointer);
    return () => window.removeEventListener('pointerdown', closeMenuOnOutsidePointer);
  }, [isOpen]);

  function toggleMenu() {
    setIsOpen((current) => !current);
  }

  function openLogoutDialog() {
    setIsOpen(false);
    setIsConfirmingLogout(true);
  }

  async function handleLogout() {
    setIsConfirmingLogout(false);
    setIsOpen(false);
    await onLogout();
  }

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="avatar-button"
        type="button"
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="用户中心"
      >
        <User size={18} strokeWidth={2.2} />
      </button>

      {isOpen ? (
        <div className="user-popover" role="menu" aria-label="用户菜单">
          {isAuthenticated ? (
            <>
              <div className="login-menu-item signed-in" role="menuitem">
                <User size={17} strokeWidth={2.2} />
                <span>{session?.email || '已登录'}</span>
              </div>
              <a className="login-menu-item" href="/usage" onClick={() => setIsOpen(false)} role="menuitem">
                <Wallet size={17} strokeWidth={2.2} />
                <span>余额和用量</span>
              </a>
              <button className="login-menu-item logout-menu-item" type="button" onClick={openLogoutDialog} role="menuitem">
                <LogOut size={17} strokeWidth={2.2} />
                <span>退出</span>
              </button>
            </>
          ) : (
            <a className="login-menu-item" href={loginHref} role="menuitem">
              <LogIn size={17} strokeWidth={2.2} />
              <span>登录</span>
            </a>
          )}
        </div>
      ) : null}

      {isConfirmingLogout ? (
        <div className="logout-dialog-backdrop" onClick={() => setIsConfirmingLogout(false)}>
          <section
            className="logout-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="logout-dialog-icon" aria-hidden="true">
              <LogOut size={20} strokeWidth={2.2} />
            </span>
            <div className="logout-dialog-copy">
              <h2 id="logout-dialog-title">确认退出登录？</h2>
              <p>退出后需要重新登录才能进入用户中心。</p>
            </div>
            <div className="logout-dialog-actions">
              <button className="logout-cancel-button" type="button" onClick={() => setIsConfirmingLogout(false)} autoFocus>
                取消
              </button>
              <button className="logout-confirm-button" type="button" onClick={handleLogout}>
                确认退出
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

type SiteHeaderProps = {
  isDocsPage: boolean;
  isLoginPage: boolean;
  isModelsPage: boolean;
  isUsagePage: boolean;
  onLogout: () => void | Promise<void>;
  session: AuthSession | null;
};

function SiteHeader({ isDocsPage, isLoginPage, isModelsPage, isUsagePage, onLogout, session }: SiteHeaderProps) {
  const homePrefix = isDocsPage || isLoginPage || isModelsPage || isUsagePage ? '/' : '';
  const loginHref = loginHrefForCurrentPage(isLoginPage);

  return (
    <header className="site-header" aria-label="官网导航">
      <Brand />
      <nav className="nav-links" aria-label="页面分区">
        <a href={`${homePrefix}#product`}>产品</a>
        <a aria-current={isModelsPage ? 'page' : undefined} href="/models">模型</a>
        <a aria-current={isDocsPage ? 'page' : undefined} href="/docs">文档</a>
        <a href={`${homePrefix}#download`}>下载</a>
      </nav>
      <UserMenu loginHref={loginHref} onLogout={onLogout} session={session} />
    </header>
  );
}

type SiteFooterProps = {
  docsHref: string;
  modelsHref: string;
};

function shouldShowIcpFiling() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase().replace(/\.$/, '');
  return hostname === 'vibeco.me';
}

function SiteFooter({ docsHref, modelsHref }: SiteFooterProps) {
  const showIcpFiling = shouldShowIcpFiling();

  return (
    <footer className={`site-footer${showIcpFiling ? ' has-icp' : ''}`}>
      <Brand compact />
      {showIcpFiling ? (
        <a className="icp-link" href="http://beian.miit.gov.cn/" target="_blank" rel="noopener">
          粤ICP备2026076688号
        </a>
      ) : null}
      <div className="footer-links">
        <a href="/#product">产品</a>
        <a href={modelsHref}>模型</a>
        <a href={docsHref}>文档</a>
      </div>
    </footer>
  );
}

type DocsTopic = 'overview' | 'chat' | 'responses' | 'messages' | 'params';

const docsTopics: Array<{ id: DocsTopic; label: string; kicker: string }> = [
  { id: 'overview', label: '快速开始', kicker: '分享 Key' },
  { id: 'chat', label: 'Chat Completions', kicker: '/v1/chat/completions' },
  { id: 'responses', label: 'Responses', kicker: '/v1/responses' },
  { id: 'messages', label: 'Messages', kicker: '/v1/messages' },
  { id: 'params', label: '参数与限制', kicker: '用量与安全' },
];

function DocsPage() {
  const [activeTopic, setActiveTopic] = useState<DocsTopic>('overview');
  const topic = docsTopics.find((item) => item.id === activeTopic) ?? docsTopics[0];

  return (
    <main className="docs-page" id="top" aria-label="文档">
      <section className="section docs-section">
        <div className="docs-layout">
          <aside className="docs-sidebar" aria-label="文档目录">
            <div className="docs-sidebar-title">
              <FileCode2 size={18} strokeWidth={2.2} />
              <span>分享 Key</span>
            </div>
            {docsTopics.map((item) => (
              <button
                className={`docs-nav-item ${activeTopic === item.id ? 'active' : ''}`}
                key={item.id}
                type="button"
                onClick={() => setActiveTopic(item.id)}
              >
                <span>{item.label}</span>
                <small>{item.kicker}</small>
              </button>
            ))}
          </aside>

          <article className="docs-panel docs-article-panel" aria-labelledby="docs-title">
            <div className="docs-panel-head">
              <div>
                <p className="eyebrow">{topic.kicker}</p>
                <h2 id="docs-title">{topic.label}</h2>
              </div>
            </div>
            {activeTopic === 'overview' ? <DocsOverview /> : null}
            {activeTopic === 'chat' ? <DocsChatCompletions /> : null}
            {activeTopic === 'responses' ? <DocsResponses /> : null}
            {activeTopic === 'messages' ? <DocsMessages /> : null}
            {activeTopic === 'params' ? <DocsParams /> : null}
          </article>
        </div>
      </section>
    </main>
  );
}

function DocsOverview() {
  return (
    <div className="docs-article">
      <section>
        <h3>分享 Key 是什么</h3>
        <p>
          分享 Key 用于在支持自定义 API 地址和 API Key 的外部工具中调用 Vibeco Studio 的模型转发接口。
          它不是登录凭证，也不能访问你的账户资料；它只负责让外部工具使用 Vibeco 将模型请求转发到上游模型商。
        </p>
        <p>
          通过分享 Key 发出的请求会消耗你的 Vibeco 余额。完整 Key 只会在生成成功后显示一次，
          请立即复制并保存；页面刷新后只能看到 Key 预览，不能再次查看完整内容。
        </p>
      </section>

      <section>
        <h3>创建分享 Key</h3>
        <ol>
          <li>登录 Vibeco 账号。</li>
          <li>进入“余额和用量”。</li>
          <li>点击余额右侧的“分享”。</li>
          <li>填写标题，选择到期时间或“永不过期”。</li>
          <li>点击“生成”，复制并保存完整 Key。</li>
        </ol>
      </section>

      <section>
        <h3>基础配置</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>OpenAI Base URL</th>
                <td><code>https://api.vibecostudio.com/v1</code></td>
              </tr>
              <tr>
                <th>Anthropic Base URL</th>
                <td><code>https://api.vibecostudio.com</code></td>
              </tr>
              <tr>
                <th>API Key</th>
                <td><code>vsk_...</code></td>
              </tr>
              <tr>
                <th>鉴权方式</th>
                <td>
                  OpenAI 兼容工具通常使用 <code>Authorization: Bearer YOUR_SHARE_KEY</code>。
                  Anthropic 兼容工具通常使用 <code>x-api-key: YOUR_SHARE_KEY</code>。
                  二选一即可，不建议同时传。
                </td>
              </tr>
              <tr>
                <th>模型名称</th>
                <td>使用“模型”页面中展示的模型 ID。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>权限范围</h3>
        <p>分享 Key 只能用于模型转发接口，不能访问余额、用量、分享 Key 管理、登录状态等账户接口。</p>
        <ul>
          <li><code>/v1/chat/completions</code></li>
          <li><code>/v1/responses</code></li>
          <li><code>/v1/messages</code></li>
          <li><code>/v1/images</code></li>
        </ul>
      </section>

      <section>
        <h3>怎么选择接口</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>Chat Completions</th>
                <td>
                  兼容面最广。外部工具只让你填写 OpenAI Base URL 和 API Key 时，优先使用
                  <code>https://api.vibecostudio.com/v1</code>。
                </td>
              </tr>
              <tr>
                <th>Responses</th>
                <td>
                  适合支持 OpenAI Responses API 的新客户端。它更适合推理、内置工具和结构化输出。
                </td>
              </tr>
              <tr>
                <th>Messages</th>
                <td>
                  适合要求 Anthropic Messages 格式的工具。Base URL 使用
                  <code>https://api.vibecostudio.com</code>，请求路径由 SDK 自动拼接为 <code>/v1/messages</code>。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DocsChatCompletions() {
  return (
    <div className="docs-article">
      <section>
        <h3>适用场景</h3>
        <p>
          <code>/v1/chat/completions</code> 适合 OpenAI Chat Completions 兼容工具。
          大多数支持自定义 OpenAI Base URL 的聊天客户端、IDE 插件和自动化脚本都可以优先使用这个接口。
        </p>
        <p>
          这个接口使用 <code>messages</code> 数组描述对话历史，返回结构中最常用的文本位置是
          <code>choices[0].message.content</code>。如果你的工具已经按 OpenAI Chat Completions 写好，
          通常只需要替换 Base URL、API Key 和模型名。
        </p>
      </section>

      <section>
        <h3>请求结构</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>方法</th>
                <td><code>POST</code></td>
              </tr>
              <tr>
                <th>路径</th>
                <td><code>/v1/chat/completions</code></td>
              </tr>
              <tr>
                <th>核心字段</th>
                <td><code>model</code>、<code>messages</code></td>
              </tr>
              <tr>
                <th>消息角色</th>
                <td>
                  常见角色是 <code>system</code>、<code>user</code>、<code>assistant</code>。
                  工具调用场景还可能包含客户端自己的工具消息结构。
                </td>
              </tr>
              <tr>
                <th>输出上限</th>
                <td><code>max_completion_tokens</code> 或 <code>max_tokens</code></td>
              </tr>
              <tr>
                <th>采样参数</th>
                <td><code>temperature</code> 支持 <code>0</code> 到 <code>2</code>，也可以传 <code>top_p</code>。</td>
              </tr>
              <tr>
                <th>工具调用</th>
                <td>
                  可传 <code>tools</code>、<code>tool_choice</code>、<code>parallel_tool_calls</code>。
                  是否真的触发工具调用取决于所选模型的能力和客户端实现。
                </td>
              </tr>
              <tr>
                <th>读取结果</th>
                <td><code>choices[0].message.content</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>字段兼容</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>常用可传</th>
                <td>
                  <code>model</code>、<code>messages</code>、<code>max_tokens</code>、
                  <code>max_completion_tokens</code>、<code>stream</code>、<code>stream_options</code>、
                  <code>temperature</code>、<code>top_p</code>、<code>stop</code>、
                  <code>logprobs</code>、<code>top_logprobs</code>。
                </td>
              </tr>
              <tr>
                <th>工具字段</th>
                <td>
                  <code>tools</code>、<code>tool_choice</code>、<code>parallel_tool_calls</code> 会按兼容格式转发。
                  <code>strict</code> 约束不保证所有模型都会严格遵守。
                </td>
              </tr>
              <tr>
                <th>数量限制</th>
                <td><code>n</code> 必须是 <code>1</code>。返回的 <code>choices</code> 通常只有一个结果。</td>
              </tr>
              <tr>
                <th>模型特定参数</th>
                <td>
                  推理强度、思考预算、图片比例、视频尺寸等模型特定参数建议放在 SDK 的
                  <code>extra_body</code> 中，或按工具支持的自定义字段传递。
                </td>
              </tr>
              <tr>
                <th>不建议依赖</th>
                <td>
                  <code>store</code>、<code>metadata</code>、<code>response_format</code>、
                  <code>presence_penalty</code>、<code>frequency_penalty</code>、<code>seed</code>、
                  <code>service_tier</code>、<code>logit_bias</code>、<code>user</code> 等字段可能被兼容层忽略。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>cURL 示例</h3>
        <pre><code>{`curl "https://api.vibecostudio.com/v1/chat/completions" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_SHARE_KEY" \\
  -d '{
    "model": "Claude-Sonnet-4.6",
    "messages": [
      {"role": "system", "content": "你是一个简洁的中文助手。"},
      {"role": "user", "content": "解释什么是分享 Key。"}
    ],
    "max_completion_tokens": 800
  }'`}</code></pre>
      </section>

      <section>
        <h3>Python 示例</h3>
        <pre><code>{`from openai import OpenAI

client = OpenAI(
    api_key="YOUR_SHARE_KEY",
    base_url="https://api.vibecostudio.com/v1",
)

chat = client.chat.completions.create(
    model="Claude-Sonnet-4.6",
    messages=[
        {"role": "user", "content": "用一句话介绍 Vibeco Studio。"}
    ],
    max_completion_tokens=800,
)

print(chat.choices[0].message.content)`}</code></pre>
      </section>

      <section>
        <h3>流式输出</h3>
        <p>
          客户端和模型支持流式时，可以传 <code>stream: true</code>。流式响应适合聊天 UI，
          但具体分片速度取决于客户端、模型和网络链路。
        </p>
        <pre><code>{`stream = client.chat.completions.create(
    model="Claude-Sonnet-4.6",
    messages=[
        {"role": "user", "content": "写一段 100 字以内的产品介绍。"}
    ],
    stream=True,
)

for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="", flush=True)`}</code></pre>
      </section>

      <section>
        <h3>图片与文件输入</h3>
        <p>
          支持多模态的模型可以在 <code>messages[].content</code> 中接收文本、图片 URL 或文件数据。
          文件通常使用 <code>data:</code> URL 传递 base64 内容，公开可访问的图片 URL 也可以直接传入。
          图片、视频、音频生成类模型建议使用非流式请求，稳定性通常更好。
        </p>
        <pre><code>{`chat = client.chat.completions.create(
    model="Claude-Sonnet-4.6",
    messages=[
        {
            "role": "user",
            "content": [
                {"type": "text", "text": "请描述这张图片。"},
                {
                    "type": "image_url",
                    "image_url": {
                        "url": "https://example.com/image.png"
                    }
                },
                {
                    "type": "file",
                    "file": {
                        "filename": "guide.pdf",
                        "file_data": "data:application/pdf;base64,BASE64_CONTENT"
                    }
                }
            ],
        }
    ],
)`}</code></pre>
      </section>

      <section>
        <h3>返回结构</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>文本</th>
                <td><code>choices[0].message.content</code></td>
              </tr>
              <tr>
                <th>工具调用</th>
                <td><code>choices[0].message.tool_calls</code>，仅在模型实际发起工具调用时出现。</td>
              </tr>
              <tr>
                <th>结束原因</th>
                <td><code>choices[0].finish_reason</code></td>
              </tr>
              <tr>
                <th>用量</th>
                <td>
                  <code>usage.prompt_tokens</code>、<code>usage.completion_tokens</code>、
                  <code>usage.total_tokens</code>。最终费用以 Vibeco 使用明细为准。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DocsResponses() {
  return (
    <div className="docs-article">
      <section>
        <h3>适用场景</h3>
        <p>
          <code>/v1/responses</code> 适合 OpenAI Responses API 兼容工具。
          它用 <code>input</code> 表达用户输入，适合需要统一文本、图片和结构化输入的新客户端。
        </p>
        <p>
          相比 Chat Completions，它更适合把推理、网页搜索和结构化输出作为一等能力来配置。
          如果你的 SDK 已经支持 <code>client.responses.create</code>，可以优先考虑这个接口。
        </p>
      </section>

      <section>
        <h3>请求结构</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>方法</th>
                <td><code>POST</code></td>
              </tr>
              <tr>
                <th>路径</th>
                <td><code>/v1/responses</code></td>
              </tr>
              <tr>
                <th>核心字段</th>
                <td><code>model</code>、<code>input</code></td>
              </tr>
              <tr>
                <th>输入形式</th>
                <td><code>input</code> 可以是字符串，也可以是客户端 SDK 支持的结构化输入数组。</td>
              </tr>
              <tr>
                <th>输出上限</th>
                <td><code>max_output_tokens</code></td>
              </tr>
              <tr>
                <th>高级能力</th>
                <td>
                  可按模型能力传 <code>reasoning</code>、<code>tools</code>、<code>text.format</code> 等字段。
                </td>
              </tr>
              <tr>
                <th>读取结果</th>
                <td>优先读取 SDK 提供的 <code>output_text</code>，或从返回的 <code>output</code> 内容中提取文本。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>和 Chat Completions 的区别</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>调用方法</th>
                <td><code>client.responses.create(...)</code>，对应路径 <code>/v1/responses</code>。</td>
              </tr>
              <tr>
                <th>输入字段</th>
                <td>
                  使用 <code>input</code>，可以是字符串，也可以是 SDK 支持的结构化输入。
                  Chat Completions 使用 <code>messages</code>。
                </td>
              </tr>
              <tr>
                <th>输出读取</th>
                <td>
                  Responses 通常读取 <code>response.output_text</code>。
                  Chat Completions 通常读取 <code>choices[0].message.content</code>。
                </td>
              </tr>
              <tr>
                <th>推理参数</th>
                <td>
                  Responses 使用 <code>reasoning</code>，例如 <code>{'{"effort":"high","summary":"auto"}'}</code>。
                  Chat Completions 中类似能力通常放在 <code>extra_body</code>。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>cURL 示例</h3>
        <pre><code>{`curl "https://api.vibecostudio.com/v1/responses" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_SHARE_KEY" \\
  -d '{
    "model": "GPT-5.4",
    "input": "用三条要点说明如何安全保存分享 Key。",
    "max_output_tokens": 800
  }'`}</code></pre>
      </section>

      <section>
        <h3>Node.js 示例</h3>
        <pre><code>{`import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.VIBECO_SHARE_KEY,
  baseURL: "https://api.vibecostudio.com/v1",
});

const response = await client.responses.create({
  model: "GPT-5.4",
  input: "写一个分享 Key 的安全使用清单。",
  max_output_tokens: 800,
});

console.log(response.output_text);`}</code></pre>
      </section>

      <section>
        <h3>推理模式</h3>
        <p>
          对复杂分析、代码规划、数学题或需要长链路判断的任务，可以传 <code>reasoning</code>。
          不是所有模型都支持推理字段；不支持时可能被忽略，或退化为普通回答。
        </p>
        <pre><code>{`const response = await client.responses.create({
  model: "Claude-Sonnet-4.6",
  input: "一步步分析这个接口设计是否合理。",
  reasoning: {
    effort: "high",
    summary: "auto",
  },
});`}</code></pre>
      </section>

      <section>
        <h3>网页搜索工具</h3>
        <p>
          支持工具能力的模型可以通过 <code>tools</code> 启用内置网页搜索。
          适合获取近期信息，但仍建议在关键场景中要求模型返回来源并自行核验。
        </p>
        <pre><code>{`const response = await client.responses.create({
  model: "GPT-5.4",
  input: "总结今天与 AI 编程工具有关的三条新闻。",
  tools: [{ type: "web_search_preview" }],
});`}</code></pre>
      </section>

      <section>
        <h3>结构化输出</h3>
        <p>
          Responses 使用 <code>text.format</code> 描述 JSON Schema。不要把 Chat Completions 的
          <code>response_format</code> 当作等价能力依赖；不同模型对结构化输出的遵循程度可能不同。
        </p>
        <pre><code>{`const response = await client.responses.create({
  model: "GPT-5.4",
  input: "列出三个适合使用分享 Key 的工具类型。",
  text: {
    format: {
      type: "json_schema",
      name: "tool_types",
      schema: {
        type: "object",
        properties: {
          tools: {
            type: "array",
            items: {
              type: "object",
              properties: {
                name: { type: "string" },
                reason: { type: "string" },
              },
              required: ["name", "reason"],
            },
          },
        },
        required: ["tools"],
      },
    },
  },
});`}</code></pre>
      </section>
    </div>
  );
}

function DocsMessages() {
  return (
    <div className="docs-article">
      <section>
        <h3>适用场景</h3>
        <p>
          <code>/v1/messages</code> 适合 Anthropic Messages API 兼容工具。
          OpenCode、部分 IDE 插件或代理工具如果要求 Anthropic 格式，可以配置到这个接口。
        </p>
        <p>
          这个接口主要面向 Claude/Anthropic Messages 生态。非 Claude 类模型或 OpenAI 风格客户端，
          建议使用 <code>/v1/chat/completions</code> 或 <code>/v1/responses</code>。
        </p>
      </section>

      <section>
        <h3>兼容配置</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>Base URL</th>
                <td><code>https://api.vibecostudio.com</code></td>
              </tr>
              <tr>
                <th>方法</th>
                <td><code>POST</code></td>
              </tr>
              <tr>
                <th>路径</th>
                <td><code>/v1/messages</code></td>
              </tr>
              <tr>
                <th>鉴权</th>
                <td>
                  推荐 <code>x-api-key: YOUR_SHARE_KEY</code>。
                  如果工具只支持 Bearer，也可以使用 <code>Authorization: Bearer YOUR_SHARE_KEY</code>。
                </td>
              </tr>
              <tr>
                <th>版本头</th>
                <td>
                  建议传 <code>anthropic-version: 2023-06-01</code>。
                  支持 beta 功能的工具也可能传 <code>anthropic-beta</code>。
                </td>
              </tr>
              <tr>
                <th>模型名称</th>
                <td>
                  使用“模型”页面中 Claude/Anthropic Messages 兼容的模型 ID。
                  如果一个模型无法通过此接口调用，请改用 Chat Completions 或 Responses。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>请求结构</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>核心字段</th>
                <td><code>model</code>、<code>messages</code>、<code>max_tokens</code></td>
              </tr>
              <tr>
                <th>系统提示</th>
                <td>通常放在顶层 <code>system</code> 字段，而不是放进 <code>messages</code>。</td>
              </tr>
              <tr>
                <th>消息角色</th>
                <td><code>user</code>、<code>assistant</code>。系统提示放在顶层 <code>system</code>。</td>
              </tr>
              <tr>
                <th>内容块</th>
                <td>
                  文本内容可以直接使用字符串；复杂输入可以按 Anthropic Messages SDK 支持的内容块传入。
                </td>
              </tr>
              <tr>
                <th>流式</th>
                <td>传 <code>stream: true</code>，客户端按 SSE 或 SDK 提供的事件流读取。</td>
              </tr>
              <tr>
                <th>读取结果</th>
                <td>读取返回的 <code>content</code> 文本块。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>cURL 示例</h3>
        <pre><code>{`curl "https://api.vibecostudio.com/v1/messages" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_SHARE_KEY" \\
  -H "anthropic-version: 2023-06-01" \\
  -d '{
    "model": "Claude-Sonnet-4.6",
    "system": "你是一个简洁的中文助手。",
    "messages": [
      {"role": "user", "content": "给我一个分享 Key 的使用检查清单。"}
    ],
    "max_tokens": 800
  }'`}</code></pre>
      </section>

      <section>
        <h3>Python SDK 示例</h3>
        <pre><code>{`import anthropic

client = anthropic.Anthropic(
    api_key="YOUR_SHARE_KEY",
    base_url="https://api.vibecostudio.com",
)

message = client.messages.create(
    model="Claude-Sonnet-4.6",
    system="你是一个简洁的中文助手。",
    messages=[
        {"role": "user", "content": "解释 Vibeco 分享 Key 的权限范围。"}
    ],
    max_tokens=800,
)

print(message.content[0].text)`}</code></pre>
      </section>

      <section>
        <h3>Node.js SDK 示例</h3>
        <pre><code>{`import Anthropic from "@anthropic-ai/sdk";

const client = new Anthropic({
  apiKey: process.env.VIBECO_SHARE_KEY,
  baseURL: "https://api.vibecostudio.com",
});

const message = await client.messages.create({
  model: "Claude-Sonnet-4.6",
  max_tokens: 800,
  messages: [
    { role: "user", content: "写一段分享 Key 的使用说明。" },
  ],
});

console.log(message.content[0].text);`}</code></pre>
      </section>

      <section>
        <h3>流式输出</h3>
        <p>
          Anthropic Messages 兼容客户端通常通过 SDK 的 stream 方法，或 SSE 事件读取增量内容。
          如果流中途发生错误，客户端可能收到一个错误事件后连接关闭。
        </p>
        <pre><code>{`const stream = await client.messages.stream({
  model: "Claude-Sonnet-4.6",
  max_tokens: 800,
  messages: [
    { role: "user", content: "用中文写一段简短欢迎语。" },
  ],
});

for await (const event of stream) {
  if (event.type === "content_block_delta" && event.delta.type === "text_delta") {
    process.stdout.write(event.delta.text);
  }
}`}</code></pre>
      </section>

      <section>
        <h3>注意事项</h3>
        <ul>
          <li>如果工具要求填写 Anthropic Base URL，通常填写 <code>https://api.vibecostudio.com</code>。</li>
          <li>如果工具要求填写完整接口地址，则填写 <code>https://api.vibecostudio.com/v1/messages</code>。</li>
          <li>如果同时传 <code>Authorization</code> 和 <code>x-api-key</code>，不同网关可能有不同优先级；建议只传一种。</li>
          <li>模型是否支持工具调用、图片输入、长上下文等能力，以“模型”页面和实际返回为准。</li>
        </ul>
      </section>
    </div>
  );
}

function DocsParams() {
  return (
    <div className="docs-article">
      <section>
        <h3>参数支持速查</h3>
        <p>
          Vibeco 会按兼容接口把请求转发到模型服务。不同模型的能力并不完全相同；
          同一个字段可能在一个模型上生效，在另一个模型上被忽略。
        </p>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th>模型与输入</th>
                <td>
                  <code>model</code>、<code>messages</code>、<code>input</code>、<code>system</code>
                  等字段按对应接口格式使用。
                </td>
              </tr>
              <tr>
                <th>输出长度</th>
                <td>
                  Chat Completions 使用 <code>max_tokens</code> 或 <code>max_completion_tokens</code>；
                  Responses 使用 <code>max_output_tokens</code>；
                  Messages 使用 <code>max_tokens</code>。
                </td>
              </tr>
              <tr>
                <th>采样</th>
                <td><code>temperature</code>、<code>top_p</code> 通常可用，具体效果取决于模型。</td>
              </tr>
              <tr>
                <th>工具调用</th>
                <td>
                  <code>tools</code>、<code>tool_choice</code>、<code>parallel_tool_calls</code> 可按兼容格式传入。
                  Responses 的网页搜索使用 <code>tools: [{'{"type":"web_search_preview"}'}]</code>。
                </td>
              </tr>
              <tr>
                <th>模型特定参数</th>
                <td>
                  Chat Completions 中可使用 <code>extra_body</code> 传推理强度、思考预算、图片比例、视频尺寸等。
                  Responses 的推理能力优先使用 <code>reasoning</code> 字段。
                </td>
              </tr>
              <tr>
                <th>多模态</th>
                <td>
                  文本、图片、文件、音频、视频能力取决于模型本身和接口格式。
                  生成图片、视频或音频时，非流式请求通常更稳。
                </td>
              </tr>
              <tr>
                <th>可能忽略</th>
                <td>
                  <code>store</code>、<code>metadata</code>、<code>response_format</code>、
                  <code>prediction</code>、<code>presence_penalty</code>、<code>frequency_penalty</code>、
                  <code>seed</code>、<code>service_tier</code>、<code>audio</code>、<code>logit_bias</code>、
                  <code>user</code>、<code>modalities</code> 等字段不要作为关键逻辑依赖。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>计费与限制</h3>
        <ul>
          <li>外部工具通过分享 Key 发出的请求会消耗 Vibeco 账户余额。</li>
          <li>分享 Key 不能查看余额和用量；请回到 Vibeco 的“余额和用量”页面查看明细。</li>
          <li>请求限流按请求数计算。返回中如果包含 <code>x-ratelimit-*</code> 头，可以用它们判断剩余额度和重置时间。</li>
          <li>高频请求可能受到模型服务或 Vibeco 网关限流；遇到 <code>429</code> 时请退避重试。</li>
          <li>模型返回缺少用量信息时，后端会按当前计费策略处理，最终以“使用明细”为准。</li>
        </ul>
      </section>

      <section>
        <h3>错误码</h3>
        <div className="docs-config-table-wrap">
          <table className="docs-config-table">
            <tbody>
              <tr>
                <th><code>400</code></th>
                <td>请求体格式错误、缺少必要字段，或字段类型不正确。</td>
              </tr>
              <tr>
                <th><code>401</code></th>
                <td>分享 Key 缺失、错误、过期或已删除。</td>
              </tr>
              <tr>
                <th><code>402</code></th>
                <td>账户余额不足，充值后再重试。</td>
              </tr>
              <tr>
                <th><code>403</code></th>
                <td>请求没有权限、被安全策略拒绝，或分享 Key 正在访问不允许的账户接口。</td>
              </tr>
              <tr>
                <th><code>404</code></th>
                <td>接口路径或模型名不存在。</td>
              </tr>
              <tr>
                <th><code>408</code></th>
                <td>模型启动或响应超时，可以稍后重试。</td>
              </tr>
              <tr>
                <th><code>413</code></th>
                <td>输入超过模型上下文或网关允许的大小。</td>
              </tr>
              <tr>
                <th><code>429</code></th>
                <td>请求过于频繁。建议读取 <code>Retry-After</code> 或 <code>x-ratelimit-reset-requests</code> 后重试。</td>
              </tr>
              <tr>
                <th><code>500</code>、<code>502</code>、<code>529</code></th>
                <td>模型服务异常、上游不可用或临时拥塞。建议使用指数退避和随机抖动重试。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>重试建议</h3>
        <ul>
          <li>对 <code>429</code>、<code>500</code>、<code>502</code>、<code>529</code> 使用指数退避，例如从 250ms 开始。</li>
          <li>给退避时间增加随机抖动，避免多个任务同时重试。</li>
          <li>如果请求会触发外部副作用，请在客户端保存请求 ID 或业务 ID，避免重试造成重复操作。</li>
          <li>记录 <code>x-request-id</code>、HTTP 状态码和模型名，方便排查问题。</li>
        </ul>
      </section>

      <section>
        <h3>安全建议</h3>
        <ul>
          <li>不要把分享 Key 写进公开仓库、前端代码、日志或截图里。</li>
          <li>给不同工具创建不同标题的 Key，方便以后识别和删除。</li>
          <li>不再使用的 Key 应及时在“余额和用量”中删除。</li>
          <li>建议给临时用途的 Key 设置到期时间。</li>
        </ul>
      </section>
    </div>
  );
}

type ModelsPageProps = {
  isModelsLoading: boolean;
  models: ModelRow[];
  modelsStatus: string;
};

function ModelsPage({ isModelsLoading, models, modelsStatus }: ModelsPageProps) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [copiedModelId, setCopiedModelId] = useState('');
  const sortedModels = useMemo(() => {
    if (sortKey === null) return models;

    const costField = sortKey === 'input' ? 'inputCostPerMillionTokens' : 'outputCostPerMillionTokens';
    return [...models].sort((first, second) => {
      const costComparison = compareCosts(first[costField], second[costField], sortDirection);
      if (costComparison !== 0) return costComparison;
      return first.id.localeCompare(second.id);
    });
  }, [models, sortDirection, sortKey]);

  function toggleSort(nextSortKey: SortKey) {
    if (sortKey !== nextSortKey) {
      setSortKey(nextSortKey);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }

    setSortKey(null);
    setSortDirection('asc');
  }

  function renderSortIcon(key: SortKey) {
    if (sortKey !== key) {
      return <ArrowUpDown size={15} strokeWidth={2.2} />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={15} strokeWidth={2.2} /> : <ArrowDown size={15} strokeWidth={2.2} />;
  }

  async function copyModelId(modelId: string) {
    try {
      await copyTextToClipboard(modelId);
      setCopiedModelId(modelId);
      window.setTimeout(() => {
        setCopiedModelId((currentModelId) => (currentModelId === modelId ? '' : currentModelId));
      }, 1600);
    } catch {
      setCopiedModelId('');
    }
  }

  return (
    <main className="docs-page" id="top" aria-label="模型">
      <section className="section docs-section">
        <div className="models-layout">
          <article className="docs-panel" aria-labelledby="models-title">
            <div className="docs-panel-head">
              <div>
                <h2 id="models-title">模型</h2>
              </div>
              <span>
                {isModelsLoading ? (
                  <>
                    <LoaderCircle size={14} strokeWidth={2.2} />
                    Loading
                  </>
                ) : (
                  `${models.length} 个模型`
                )}
              </span>
            </div>

            <div className="model-table-wrap">
              <table className="model-table">
                <thead>
                  <tr>
                    <th>模型</th>
                    <th>供应方</th>
                    <th aria-sort={sortKey === 'input' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button className="model-sort-button" type="button" onClick={() => toggleSort('input')} disabled={isModelsLoading}>
                        <span>输入成本 / 100万 token (USD)</span>
                        {renderSortIcon('input')}
                      </button>
                    </th>
                    <th aria-sort={sortKey === 'output' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button className="model-sort-button" type="button" onClick={() => toggleSort('output')} disabled={isModelsLoading}>
                        <span>输出成本 / 100万 token (USD)</span>
                        {renderSortIcon('output')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isModelsLoading ? (
                    <tr>
                      <td className="model-table-loading" colSpan={4}>
                        <span className="loading-state" role="status" aria-label="Loading">
                          <LoaderCircle size={19} strokeWidth={2.2} />
                          <span>Loading</span>
                        </span>
                      </td>
                    </tr>
                  ) : (
                    sortedModels.map((model) => {
                      const usesOfficialOriginalForBoth = model.inputUsesOriginalPrice && model.outputUsesOriginalPrice;
                      return (
                        <tr key={model.id}>
                          <td>
                            <span className="model-id-cell">
                              <button
                                className="model-id-button"
                                type="button"
                                onClick={() => copyModelId(model.id)}
                                aria-label={`复制 ${model.id}`}
                                title="点击复制"
                              >
                                <code>{model.id}</code>
                                <span className="copy-feedback" aria-hidden={copiedModelId !== model.id}>
                                  已复制
                                </span>
                              </button>
                              {usesOfficialOriginalForBoth ? <span className="model-price-tag">官方原价</span> : null}
                            </span>
                          </td>
                          <td>{model.owner}</td>
                          <td className="model-price-cell">
                            <ModelPrice isOriginal={model.inputUsesOriginalPrice && !usesOfficialOriginalForBoth} value={model.inputCostPerMillionTokens} />
                          </td>
                          <td className="model-price-cell">
                            <ModelPrice isOriginal={model.outputUsesOriginalPrice && !usesOfficialOriginalForBoth} value={model.outputCostPerMillionTokens} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <p className="docs-status" aria-live="polite">{isModelsLoading ? '' : modelsStatus}</p>
          </article>
        </div>
      </section>
    </main>
  );
}

type ShareKeyDialogProps = {
  error: string;
  form: ShareKeyFormState;
  isSubmitting: boolean;
  onCancel: () => void;
  onChange: (form: ShareKeyFormState) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
};

function ShareKeyDialog({ error, form, isSubmitting, onCancel, onChange, onSubmit }: ShareKeyDialogProps) {
  const canSubmit = form.title.trim() && (form.neverExpires || form.expiresAt);

  return (
    <div className="share-key-dialog-backdrop">
      <section className="share-key-dialog" role="dialog" aria-modal="true" aria-labelledby="share-key-dialog-title">
        <form className="share-key-dialog-form" onSubmit={onSubmit}>
          <div className="share-key-dialog-head">
            <h2 id="share-key-dialog-title">生成分享 Key</h2>
            <p>完整 Key 只会显示一次，生成后请立即保存。</p>
          </div>

          <label className="share-key-field">
            <span>标题</span>
            <input
              type="text"
              value={form.title}
              onChange={(event) => onChange({ ...form, title: event.target.value })}
              placeholder="例如：Cherry Studio"
              maxLength={80}
              autoFocus
              disabled={isSubmitting}
            />
          </label>

          <label className="share-key-checkbox">
            <input
              type="checkbox"
              checked={form.neverExpires}
              onChange={(event) => onChange({ ...form, neverExpires: event.target.checked })}
              disabled={isSubmitting}
            />
            <span>永不过期</span>
          </label>

          {!form.neverExpires ? (
            <label className="share-key-field">
              <span>到期时间</span>
              <input
                type="datetime-local"
                value={form.expiresAt}
                min={formatDateTimeLocal(new Date())}
                onChange={(event) => onChange({ ...form, expiresAt: event.target.value })}
                disabled={isSubmitting}
              />
            </label>
          ) : null}

          {error ? <p className="share-key-dialog-error" role="alert">{error}</p> : null}

          <div className="share-key-dialog-actions">
            {!isSubmitting ? (
              <button className="secondary-button" type="button" onClick={onCancel}>
                取消
              </button>
            ) : null}
            <button className="primary-button" type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoaderCircle size={16} strokeWidth={2.2} />
                  <span>生成中</span>
                </>
              ) : (
                <span>生成</span>
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

type RechargeDialogProps = {
  amount: string;
  error: string;
  isPaymentChecking: boolean;
  isRateLoading: boolean;
  isSubmitting: boolean;
  onAmountChange: (amount: string) => void;
  onCancel: () => void;
  onCheckNow: () => void;
  onConfirm: (event: FormEvent<HTMLFormElement>) => void;
  onReopenPayment: () => void;
  pendingPayment: AlipayPaymentOrder | null;
  rate: RechargeExchangeRate | null;
};

function RechargeDialog({
  amount,
  error,
  isPaymentChecking,
  isRateLoading,
  isSubmitting,
  onAmountChange,
  onCancel,
  onCheckNow,
  onConfirm,
  onReopenPayment,
  pendingPayment,
  rate,
}: RechargeDialogProps) {
  const usdAmount = parseRechargeUsdAmount(amount);
  const payableCny = rate && usdAmount > 0 ? usdAmount * rate.chargedRate : 0;
  const canConfirm = Boolean(usdAmount > 0 && rate && payableCny > 0 && !isRateLoading && !isSubmitting && !pendingPayment);
  const isPaymentPending = Boolean(pendingPayment && pendingPayment.status !== 'paid');
  const payableLabel = pendingPayment
    ? formatCnyAmount(pendingPayment.totalAmountCny)
    : rate && payableCny > 0
      ? formatCnyAmount(payableCny)
      : '--';

  return (
    <div className="share-key-dialog-backdrop recharge-dialog-backdrop">
      <section className="share-key-dialog recharge-dialog" role="dialog" aria-modal="true" aria-labelledby="recharge-dialog-title">
        <form className="share-key-dialog-form recharge-dialog-form" onSubmit={onConfirm}>
          <div className="share-key-dialog-head">
            <h2 id="recharge-dialog-title">充值</h2>
          </div>

          <label className="recharge-dollar-row" aria-label="美元充值金额">
            <span className="recharge-currency-symbol">$</span>
            <input
              autoFocus
              disabled={Boolean(pendingPayment)}
              inputMode="decimal"
              min="0"
              onChange={(event) => onAmountChange(event.target.value)}
              placeholder="输入美元金额"
              step="0.01"
              type="number"
              value={amount}
            />
          </label>

          <div className="recharge-rate-row" title={rate ? `${rate.source}${rate.updatedAt ? ` · ${rate.updatedAt}` : ''}` : undefined}>
            {isRateLoading ? (
              <span className="recharge-rate-status">
                <LoaderCircle size={15} strokeWidth={2.2} />
                Loading
              </span>
            ) : rate ? (
              <span className="recharge-rate-value">汇率：{formatCnyRate(rate.chargedRate)}</span>
            ) : (
              <span className="recharge-rate-value">汇率：--</span>
            )}
          </div>

          <div className="recharge-pay-row">
            <span>需要支付人民币</span>
            <strong>{payableLabel}</strong>
          </div>

          {pendingPayment ? (
            <div className="recharge-payment-state" role="status" aria-live="polite">
              <div>
                <strong>{pendingPayment.status === 'paid' ? '充值已到账' : '支付页已打开'}</strong>
                <span>{pendingPayment.outTradeNo}</span>
              </div>
              <span>
                {isPaymentChecking ? (
                  <>
                    <LoaderCircle className="recharge-spinner" size={14} strokeWidth={2.2} />
                    正在查询
                  </>
                ) : pendingPayment.status === 'paid' ? (
                  '已完成'
                ) : (
                  '等待支付'
                )}
              </span>
            </div>
          ) : null}

          {error ? <p className="share-key-dialog-error" role="alert">{error}</p> : null}

          <div className="share-key-dialog-actions recharge-dialog-actions">
            <button className="secondary-button" type="button" onClick={onCancel}>
              取消
            </button>
            {isPaymentPending ? (
              <>
                <button className="secondary-button" type="button" onClick={onReopenPayment}>
                  <ExternalLink size={15} strokeWidth={2.2} />
                  <span>重新打开</span>
                </button>
                <button className="primary-button" type="button" onClick={onCheckNow} disabled={isPaymentChecking}>
                  {isPaymentChecking ? <LoaderCircle className="recharge-spinner" size={15} strokeWidth={2.2} /> : <RefreshCw size={15} strokeWidth={2.2} />}
                  <span>立即查询</span>
                </button>
              </>
            ) : (
              <button className="primary-button" type="submit" disabled={!canConfirm}>
                {isSubmitting ? (
                  <>
                    <LoaderCircle className="recharge-spinner" size={15} strokeWidth={2.2} />
                    <span>创建订单</span>
                  </>
                ) : (
                  <span>确认支付</span>
                )}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}

type RechargeRecordsPanelProps = {
  canGoPrevious: boolean;
  isLoading: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
  page: RechargeRecordPage;
  pageIndex: number;
  status: string;
};

function rechargeRecordSource(record: RechargeRecord) {
  const provider = readText(record.metadata.provider, '');
  if (provider === 'alipay') {
    return {
      detail: readText(record.metadata.out_trade_no, record.externalId),
      title: '支付宝',
    };
  }
  return {
    detail: record.externalId,
    title: record.memo || '手动充值',
  };
}

function rechargeRecordCnyLabel(record: RechargeRecord) {
  const cnyAmount = readNumber(record.metadata.order_total_amount_cny ?? record.metadata.alipay_total_amount_cny);
  return cnyAmount > 0 ? `实付 ${formatCnyAmount(cnyAmount)}` : '';
}

function RechargeRecordsPanel({
  canGoPrevious,
  isLoading,
  onNext,
  onPrevious,
  onRefresh,
  page,
  pageIndex,
  status,
}: RechargeRecordsPanelProps) {
  return (
    <article className="usage-panel recharge-records-panel" aria-labelledby="recharge-records-title" aria-busy={isLoading}>
      <div className="usage-panel-head recharge-records-head">
        <div>
          <h2 id="recharge-records-title">充值记录</h2>
          <p>最近到账 · 第 {pageIndex + 1} 页</p>
        </div>
        <div className="recharge-records-actions">
          {isLoading ? (
            <span className="usage-panel-loading">
              <LoaderCircle size={14} strokeWidth={2.2} />
              Loading
            </span>
          ) : status ? (
            <span className="share-keys-status">{status}</span>
          ) : null}
          <button className="secondary-button recharge-records-refresh" type="button" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw size={15} strokeWidth={2.2} />
            <span>刷新</span>
          </button>
        </div>
      </div>

      <div className="usage-table-wrap">
        <table className="usage-table recharge-records-table">
          <thead>
            <tr>
              <th>时间</th>
              <th>金额</th>
              <th>来源</th>
              <th>到账后余额</th>
            </tr>
          </thead>
          <tbody>
            {page.items.length > 0 ? (
              page.items.map((item) => {
                const timestamp = formatActivityTimestamp(item.createdAt);
                const source = rechargeRecordSource(item);
                const cnyLabel = rechargeRecordCnyLabel(item);
                return (
                  <tr key={item.id}>
                    <td>
                      <span className="usage-time-cell">
                        <strong>{timestamp.date}</strong>
                        <small>{timestamp.time}</small>
                      </span>
                    </td>
                    <td className="recharge-record-amount-cell">
                      <strong>+{formatMilliCents(item.amountMilliCents)}</strong>
                      {cnyLabel ? <small>{cnyLabel}</small> : null}
                    </td>
                    <td>
                      <span className="recharge-record-source-cell">
                        <strong>{source.title}</strong>
                        {source.detail ? <small>{source.detail}</small> : null}
                      </span>
                    </td>
                    <td className="usage-cost-cell">{formatMilliCents(item.availableAfterMilliCents)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="usage-empty-row" colSpan={4}>{isLoading ? 'Loading' : '暂无充值记录'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="usage-pagination" aria-label="充值记录分页">
        <span>第 {pageIndex + 1} 页</span>
        <div>
          <button className="secondary-button" type="button" onClick={onPrevious} disabled={!canGoPrevious || isLoading}>
            上一页
          </button>
          <button className="secondary-button" type="button" onClick={onNext} disabled={!page.hasMore || !page.nextCursor || isLoading}>
            下一页
          </button>
        </div>
      </div>
    </article>
  );
}

type ShareKeysPanelProps = {
  deletingId: number | string | null;
  isLoading: boolean;
  items: ShareKeyRecord[];
  onDelete: (id: number | string) => void;
  status: string;
};

function ShareKeysPanel({ deletingId, isLoading, items, onDelete, status }: ShareKeysPanelProps) {
  const [copiedShareKeyId, setCopiedShareKeyId] = useState<number | string | null>(null);
  const hasOneTimeKey = items.some((item) => Boolean(item.key));

  async function copyShareKey(id: number | string, key: string) {
    try {
      await copyTextToClipboard(key);
      setCopiedShareKeyId(id);
      window.setTimeout(() => {
        setCopiedShareKeyId((currentId) => (currentId === id ? null : currentId));
      }, 1600);
    } catch {
      setCopiedShareKeyId(null);
    }
  }

  return (
    <article className="share-keys-panel" aria-labelledby="share-keys-title" aria-busy={isLoading}>
      <div className="share-keys-head">
        <div>
          <h2 id="share-keys-title">分享 Key</h2>
        </div>
        {isLoading ? (
          <span className="usage-panel-loading">
            <LoaderCircle size={14} strokeWidth={2.2} />
            Loading
          </span>
        ) : status ? (
          <span className="share-keys-status">{status}</span>
        ) : null}
      </div>

      {isLoading ? (
        <div className="share-keys-empty-state">
          <span className="loading-state" role="status" aria-label="Loading">
            <LoaderCircle size={19} strokeWidth={2.2} />
            <span>Loading</span>
          </span>
        </div>
      ) : items.length === 0 ? (
        <div className="share-keys-empty-state">暂无分享 Key</div>
      ) : (
        <>
          {hasOneTimeKey ? <p className="share-key-once-banner">完整 Key 只显示一次，请立即保存。</p> : null}
          <div className="share-keys-table-wrap">
            <table className="share-keys-table">
              <thead>
                <tr>
                  <th>标题</th>
                  <th>Key</th>
                  <th>到期时间</th>
                  <th>删除</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isDeleting = deletingId === item.id;
                  return (
                    <tr key={item.id}>
                      <td>{item.title}</td>
                      <td>
                        <span className="share-key-value-cell">
                          <code className="share-key-code">{item.key || item.keyPreview || '-'}</code>
                          {item.key ? (
                            <button
                              className="secondary-button share-key-copy-button"
                              type="button"
                              onClick={() => copyShareKey(item.id, item.key as string)}
                              aria-label={`复制 ${item.title} 的完整 Key`}
                            >
                              {copiedShareKeyId === item.id ? (
                                <Check size={15} strokeWidth={2.2} />
                              ) : (
                                <Copy size={15} strokeWidth={2.2} />
                              )}
                              <span>{copiedShareKeyId === item.id ? '已复制' : '复制'}</span>
                            </button>
                          ) : null}
                        </span>
                      </td>
                      <td>{formatShareKeyExpiry(item.expiresAt)}</td>
                      <td>
                        <button
                          className="secondary-button share-key-delete-button"
                          type="button"
                          onClick={() => onDelete(item.id)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? (
                            <LoaderCircle size={15} strokeWidth={2.2} />
                          ) : (
                            <Trash2 size={15} strokeWidth={2.2} />
                          )}
                          <span>{isDeleting ? '删除中' : '删除'}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </article>
  );
}

type UsagePageProps = {
  onSessionUpdated: (session: AuthSession) => void;
  session: AuthSession | null;
};

function UsagePage({ onSessionUpdated, session }: UsagePageProps) {
  const initialUsageRange = useMemo(() => defaultUsageRange(), []);
  const [accountUsage, setAccountUsage] = useState<AccountUsageData | null>(null);
  const [activityCursor, setActivityCursor] = useState('');
  const [activityCursorStack, setActivityCursorStack] = useState<string[]>([]);
  const [activityPage, setActivityPage] = useState<UsageActivityPage>({ hasMore: false, items: [], nextCursor: null });
  const [activityPageIndex, setActivityPageIndex] = useState(0);
  const [chartMode, setChartMode] = useState<UsageChartMode>('cost');
  const [deletingShareKeyId, setDeletingShareKeyId] = useState<number | string | null>(null);
  const [fromDate, setFromDate] = useState(initialUsageRange.from);
  const [isGeneratingShareKey, setIsGeneratingShareKey] = useState(false);
  const [isRechargeRecordsLoading, setIsRechargeRecordsLoading] = useState(Boolean(session));
  const [isRechargeDialogOpen, setIsRechargeDialogOpen] = useState(false);
  const [isRechargeRateLoading, setIsRechargeRateLoading] = useState(false);
  const [isRechargeSubmitting, setIsRechargeSubmitting] = useState(false);
  const [isPaymentChecking, setIsPaymentChecking] = useState(false);
  const [isShareKeyDialogOpen, setIsShareKeyDialogOpen] = useState(false);
  const [isShareKeysLoading, setIsShareKeysLoading] = useState(Boolean(session));
  const [isUsageLoading, setIsUsageLoading] = useState(Boolean(session));
  const [isActivityLoading, setIsActivityLoading] = useState(Boolean(session));
  const [rechargeAmount, setRechargeAmount] = useState('10');
  const [rechargeCursor, setRechargeCursor] = useState('');
  const [rechargeCursorStack, setRechargeCursorStack] = useState<string[]>([]);
  const [rechargeDialogError, setRechargeDialogError] = useState('');
  const [rechargePage, setRechargePage] = useState<RechargeRecordPage>({ hasMore: false, items: [], nextCursor: null });
  const [rechargePageIndex, setRechargePageIndex] = useState(0);
  const [rechargeRate, setRechargeRate] = useState<RechargeExchangeRate | null>(null);
  const [rechargeRecordsRefreshToken, setRechargeRecordsRefreshToken] = useState(0);
  const [rechargeRecordsStatus, setRechargeRecordsStatus] = useState('');
  const [pendingPayment, setPendingPayment] = useState<AlipayPaymentOrder | null>(null);
  const [shareKeyDialogError, setShareKeyDialogError] = useState('');
  const [shareKeyForm, setShareKeyForm] = useState<ShareKeyFormState>(() => defaultShareKeyForm());
  const [shareKeys, setShareKeys] = useState<ShareKeyRecord[]>([]);
  const [shareKeysStatus, setShareKeysStatus] = useState('');
  const [toDate, setToDate] = useState(initialUsageRange.to);
  const [usageStatus, setUsageStatus] = useState('');
  const usageFilters = useMemo(
    () => ({ from: fromDate, to: toDate }),
    [fromDate, toDate],
  );
  const chartBuckets = useMemo(
    () => fillUsageBuckets(fromDate, toDate, accountUsage?.buckets ?? []),
    [accountUsage, fromDate, toDate],
  );
  const totals = useMemo(() => sumUsageBuckets(chartBuckets), [chartBuckets]);
  const chartMaxValue = Math.max(
    1,
    ...chartBuckets.map((bucket) => (chartMode === 'cost' ? bucket.costMilliCents : bucket.requestCount)),
  );
  const wallet = accountUsage?.wallet ?? { availableMilliCents: 0, balanceMilliCents: 0, reservedMilliCents: 0 };

  useEffect(() => {
    let isActive = true;

    if (!session) {
      setShareKeys([]);
      setIsShareKeysLoading(false);
      setShareKeysStatus('');
      return () => {
        isActive = false;
      };
    }

    setIsShareKeysLoading(true);
    setShareKeysStatus('');

    shareKeysRequest(session)
      .then(({ items, refreshedSession }) => {
        if (!isActive) return;
        if (refreshedSession.expiresAt !== session.expiresAt) {
          writeStoredAuthSession(refreshedSession);
          onSessionUpdated(refreshedSession);
        }
        setShareKeys(items);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setShareKeys([]);
        setShareKeysStatus(error instanceof Error ? error.message : '分享 Key 加载失败。');
      })
      .finally(() => {
        if (isActive) setIsShareKeysLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [onSessionUpdated, session?.accessToken]);

  useEffect(() => {
    let isActive = true;

    if (!session) {
      setAccountUsage(null);
      setIsUsageLoading(false);
      setActivityPage({ hasMore: false, items: [], nextCursor: null });
      setIsActivityLoading(false);
      setUsageStatus('');
      return () => {
        isActive = false;
      };
    }

    if (!isValidUsageRange(fromDate, toDate)) {
      setAccountUsage(null);
      setIsUsageLoading(false);
      setUsageStatus('开始日期不能晚于结束日期。');
      return () => {
        isActive = false;
      };
    }

    setIsUsageLoading(true);
    setUsageStatus('');

    walletAndUsageSummaryRequest(session, usageFilters)
      .then(({ buckets, refreshedSession, wallet }) => {
        if (!isActive) return;
        if (refreshedSession.expiresAt !== session.expiresAt) {
          writeStoredAuthSession(refreshedSession);
          onSessionUpdated(refreshedSession);
        }
        setAccountUsage((current) => ({
          buckets,
          wallet: current && current.wallet.balanceMilliCents > wallet.balanceMilliCents ? current.wallet : wallet,
        }));
        setUsageStatus('');
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setAccountUsage(null);
        setUsageStatus(error instanceof Error ? error.message : '余额和用量加载失败。');
      })
      .finally(() => {
        if (isActive) setIsUsageLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [fromDate, onSessionUpdated, session?.accessToken, toDate, usageFilters]);

  useEffect(() => {
    let isActive = true;

    if (!session) {
      setRechargePage({ hasMore: false, items: [], nextCursor: null });
      setRechargeCursor('');
      setRechargeCursorStack([]);
      setRechargePageIndex(0);
      setRechargeRecordsStatus('');
      setIsRechargeRecordsLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsRechargeRecordsLoading(true);
    setRechargeRecordsStatus('');

    rechargeRecordsRequest(session, rechargeCursor)
      .then(({ page, refreshedSession }) => {
        if (!isActive) return;
        if (refreshedSession.expiresAt !== session.expiresAt) {
          writeStoredAuthSession(refreshedSession);
          onSessionUpdated(refreshedSession);
        }
        setRechargePage(page);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setRechargeRecordsStatus(error instanceof Error ? error.message : '充值记录加载失败。');
      })
      .finally(() => {
        if (isActive) setIsRechargeRecordsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [onSessionUpdated, rechargeCursor, rechargeRecordsRefreshToken, session?.accessToken]);

  useEffect(() => {
    let isActive = true;

    if (!session || !isValidUsageRange(fromDate, toDate)) {
      setActivityPage({ hasMore: false, items: [], nextCursor: null });
      setIsActivityLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsActivityLoading(true);

    usageActivityRequest(session, usageFilters, activityCursor)
      .then(({ page, refreshedSession }) => {
        if (!isActive) return;
        if (refreshedSession.expiresAt !== session.expiresAt) {
          writeStoredAuthSession(refreshedSession);
          onSessionUpdated(refreshedSession);
        }
        setActivityPage(page);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setActivityPage({ hasMore: false, items: [], nextCursor: null });
        setUsageStatus(error instanceof Error ? error.message : '使用明细加载失败。');
      })
      .finally(() => {
        if (isActive) setIsActivityLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [activityCursor, fromDate, onSessionUpdated, session?.accessToken, toDate, usageFilters]);

  useEffect(() => {
    let isActive = true;

    if (!isRechargeDialogOpen || pendingPayment) {
      setIsRechargeRateLoading(false);
      return () => {
        isActive = false;
      };
    }

    const usdAmount = parseRechargeUsdAmount(rechargeAmount);
    if (usdAmount <= 0) {
      setIsRechargeRateLoading(false);
      setRechargeRate(null);
      return () => {
        isActive = false;
      };
    }

    setIsRechargeRateLoading(true);
    setRechargeDialogError('');
    setRechargeRate(null);

    fetchUsdCnyExchangeRate()
      .then((rate) => {
        if (!isActive) return;
        setRechargeRate(rate);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setRechargeRate(null);
        setRechargeDialogError(error instanceof Error ? error.message : '公开汇率服务暂不可用，请稍后重试。');
      })
      .finally(() => {
        if (isActive) setIsRechargeRateLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isRechargeDialogOpen, pendingPayment, rechargeAmount]);

  useEffect(() => {
    if (!session || !isRechargeDialogOpen || !pendingPayment || pendingPayment.status === 'paid' || pendingPayment.status === 'closed') {
      return undefined;
    }

    let isActive = true;
    let timer: number | undefined;
    const outTradeNo = pendingPayment.outTradeNo;

    async function pollPayment() {
      if (!session) return;
      setIsPaymentChecking(true);
      try {
        const { order, refreshedSession } = await syncAlipayPaymentRequest(session, outTradeNo);
        if (!isActive) return;
        applySyncedAlipayPayment(order, refreshedSession);
        if (order.status !== 'paid' && order.status !== 'closed') {
          timer = window.setTimeout(pollPayment, 2600);
        }
      } catch (error) {
        if (!isActive) return;
        setRechargeDialogError(error instanceof Error ? error.message : '支付结果查询失败，稍后会继续重试。');
        timer = window.setTimeout(pollPayment, 5200);
      } finally {
        if (isActive) setIsPaymentChecking(false);
      }
    }

    timer = window.setTimeout(pollPayment, 1600);
    return () => {
      isActive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [isRechargeDialogOpen, pendingPayment?.outTradeNo, pendingPayment?.status, session?.accessToken]);

  useEffect(() => {
    if (!session) return undefined;
    const outTradeNo = readAlipayReturnOutTradeNo();
    if (!outTradeNo) return undefined;

    let isActive = true;
    setIsPaymentChecking(true);
    setUsageStatus('正在确认支付宝支付结果...');

    syncAlipayPaymentRequest(session, outTradeNo)
      .then(({ order, refreshedSession }) => {
        if (!isActive) return;
        applySyncedAlipayPayment(order, refreshedSession);
        if (order.status === 'paid' || order.status === 'closed') {
          clearAlipayReturnParams();
          return;
        }
        setPendingPayment(order);
        setRechargeDialogError('还没有查询到到账结果，支付完成后会自动刷新。');
        setIsRechargeDialogOpen(true);
        clearAlipayReturnParams();
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setUsageStatus(error instanceof Error ? error.message : '支付结果确认失败，请稍后刷新重试。');
      })
      .finally(() => {
        if (isActive) setIsPaymentChecking(false);
      });

    return () => {
      isActive = false;
    };
  }, [onSessionUpdated, session?.accessToken]);

  useEffect(() => {
    if (!session) return undefined;

    let isActive = true;
    let timer: number | undefined;
    let attempts = 0;

    async function syncPendingPayments() {
      if (!session) return;
      attempts += 1;
      try {
        const { orders, paidCount, pendingCount, refreshedSession, wallet } = await syncPendingAlipayPaymentsRequest(session);
        if (!isActive) return;
        if (refreshedSession.expiresAt !== session.expiresAt) {
          writeStoredAuthSession(refreshedSession);
          onSessionUpdated(refreshedSession);
        }
        if (wallet) {
          setAccountUsage((current) => ({
            buckets: current?.buckets ?? [],
            wallet,
          }));
        }
        if (paidCount > 0) {
          const paidAmount = orders
            .filter((order) => order.status === 'paid')
            .reduce((total, order) => total + order.amountMilliCents, 0);
          setUsageStatus(`已同步支付宝到账：${formatMilliCents(paidAmount)}。`);
          refreshRechargeRecords();
        }
        if (pendingCount > 0 && attempts < 24) {
          timer = window.setTimeout(syncPendingPayments, 5000);
        }
      } catch {
        if (isActive && attempts < 3) {
          timer = window.setTimeout(syncPendingPayments, 7000);
        }
      }
    }

    syncPendingPayments();

    return () => {
      isActive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [onSessionUpdated, session?.accessToken]);

  function resetActivityPagination() {
    setActivityCursor('');
    setActivityCursorStack([]);
    setActivityPageIndex(0);
  }

  function resetRechargePagination() {
    setRechargeCursor('');
    setRechargeCursorStack([]);
    setRechargePageIndex(0);
  }

  function refreshRechargeRecords() {
    resetRechargePagination();
    setRechargeRecordsRefreshToken((current) => current + 1);
  }

  function updateFromDate(value: string) {
    setFromDate(value);
    resetActivityPagination();
  }

  function updateToDate(value: string) {
    setToDate(value);
    resetActivityPagination();
  }

  function clearUsageFilters() {
    const nextRange = defaultUsageRange();
    setFromDate(nextRange.from);
    setToDate(nextRange.to);
    resetActivityPagination();
  }

  function goToNextActivityPage() {
    if (!activityPage.nextCursor) return;
    setActivityCursorStack((current) => [...current, activityCursor]);
    setActivityCursor(activityPage.nextCursor);
    setActivityPageIndex((current) => current + 1);
  }

  function goToPreviousActivityPage() {
    setActivityCursorStack((current) => {
      if (current.length === 0) return current;
      const nextStack = current.slice(0, -1);
      setActivityCursor(current[current.length - 1] || '');
      setActivityPageIndex((pageIndex) => Math.max(0, pageIndex - 1));
      return nextStack;
    });
  }

  function goToNextRechargePage() {
    if (!rechargePage.nextCursor) return;
    setRechargeCursorStack((current) => [...current, rechargeCursor]);
    setRechargeCursor(rechargePage.nextCursor);
    setRechargePageIndex((current) => current + 1);
  }

  function goToPreviousRechargePage() {
    setRechargeCursorStack((current) => {
      if (current.length === 0) return current;
      const nextStack = current.slice(0, -1);
      setRechargeCursor(current[current.length - 1] || '');
      setRechargePageIndex((pageIndex) => Math.max(0, pageIndex - 1));
      return nextStack;
    });
  }

  function openShareKeyDialog() {
    setShareKeyForm(defaultShareKeyForm());
    setShareKeyDialogError('');
    setIsShareKeyDialogOpen(true);
  }

  function openRechargeDialog() {
    setRechargeAmount('10');
    setRechargeDialogError('');
    setRechargeRate(null);
    setPendingPayment(null);
    setIsRechargeSubmitting(false);
    setIsPaymentChecking(false);
    setIsRechargeDialogOpen(true);
  }

  function closeRechargeDialog() {
    setIsRechargeDialogOpen(false);
    setIsPaymentChecking(false);
  }

  function reopenPendingPayment() {
    if (!pendingPayment?.paymentUrl) return;
    window.open(pendingPayment.paymentUrl, '_blank', 'noopener,noreferrer');
  }

  function applySyncedAlipayPayment(order: AlipayPaymentOrder, refreshedSession?: AuthSession) {
    if (refreshedSession && session && refreshedSession.expiresAt !== session.expiresAt) {
      writeStoredAuthSession(refreshedSession);
      onSessionUpdated(refreshedSession);
    }
    setPendingPayment((current) => {
      if (!current || current.outTradeNo !== order.outTradeNo) return current;
      return {
        ...current,
        ...order,
        paymentUrl: current.paymentUrl || order.paymentUrl,
      };
    });
    if (order.wallet) {
      setAccountUsage((current) => current ? { ...current, wallet: order.wallet as WalletBalance } : current);
    }
    if (order.status === 'paid') {
      setRechargeDialogError('');
      setUsageStatus(`充值已到账：${formatMilliCents(order.amountMilliCents)}。`);
      setIsRechargeDialogOpen(false);
      refreshRechargeRecords();
    } else if (order.status === 'closed') {
      setRechargeDialogError('支付订单已关闭，请重新发起充值。');
    }
  }

  async function checkPendingPaymentNow() {
    if (!session || !pendingPayment || isPaymentChecking) return;

    setIsPaymentChecking(true);
    setRechargeDialogError('');
    try {
      const { order, refreshedSession } = await syncAlipayPaymentRequest(session, pendingPayment.outTradeNo);
      applySyncedAlipayPayment(order, refreshedSession);
      if (order.status !== 'paid' && order.status !== 'closed') {
        setRechargeDialogError('还没有查询到到账结果，支付完成后会自动刷新。');
      }
    } catch (error) {
      setRechargeDialogError(error instanceof Error ? error.message : '支付结果查询失败，请稍后重试。');
    } finally {
      setIsPaymentChecking(false);
    }
  }

  async function submitRechargePayment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const usdAmount = parseRechargeUsdAmount(rechargeAmount);
    const amountMilliCents = parseRechargeMilliCents(rechargeAmount);
    if (usdAmount <= 0) {
      setRechargeDialogError('请输入大于 0 的美元金额。');
      return;
    }
    if (!session) {
      setRechargeDialogError('请先登录后再充值。');
      return;
    }
    if (!rechargeRate || rechargeRate.chargedRate <= 0) {
      setRechargeDialogError('正在获取汇率，请稍后再试。');
      return;
    }

    setIsRechargeSubmitting(true);
    setRechargeDialogError('');
    try {
      const totalAmountCny = formatCnyPayloadAmount(usdAmount * rechargeRate.chargedRate);
      const exchangeRate = formatExchangeRatePayload(rechargeRate.chargedRate);
      const { order, refreshedSession } = await createAlipayPaymentRequest(session, amountMilliCents, totalAmountCny, exchangeRate);
      if (!order.paymentUrl || !order.outTradeNo) {
        throw new Error('支付订单创建失败：缺少支付宝付款链接。');
      }
      if (refreshedSession.expiresAt !== session.expiresAt) {
        writeStoredAuthSession(refreshedSession);
        onSessionUpdated(refreshedSession);
      }
      setPendingPayment(order);
      setUsageStatus(`支付宝订单已创建：${formatUsdAmount(usdAmount)}，实付 ${formatCnyAmount(order.totalAmountCny)}。`);
      const openedWindow = window.open(order.paymentUrl, '_blank', 'noopener,noreferrer');
      if (!openedWindow) {
        setRechargeDialogError('支付页被浏览器拦截，请点击“重新打开”。');
      }
    } catch (error) {
      setRechargeDialogError(error instanceof Error ? error.message : '支付订单创建失败，请稍后重试。');
    } finally {
      setIsRechargeSubmitting(false);
    }
  }

  async function submitShareKey(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session) return;

    setIsGeneratingShareKey(true);
    setShareKeyDialogError('');
    try {
      const { refreshedSession, shareKey } = await createShareKeyRequest(session, shareKeyForm);
      if (refreshedSession.expiresAt !== session.expiresAt) {
        writeStoredAuthSession(refreshedSession);
        onSessionUpdated(refreshedSession);
      }
      setShareKeys((current) => [shareKey, ...current.filter((item) => item.id !== shareKey.id)]);
      setShareKeysStatus('');
      setIsShareKeyDialogOpen(false);
    } catch (error) {
      setShareKeyDialogError(error instanceof Error ? error.message : '分享 Key 生成失败。');
    } finally {
      setIsGeneratingShareKey(false);
    }
  }

  async function deleteExistingShareKey(id: number | string) {
    if (!session || deletingShareKeyId !== null) return;

    setDeletingShareKeyId(id);
    setShareKeysStatus('');
    try {
      const refreshedSession = await deleteShareKeyRequest(session, id);
      if (refreshedSession.expiresAt !== session.expiresAt) {
        writeStoredAuthSession(refreshedSession);
        onSessionUpdated(refreshedSession);
      }
      setShareKeys((current) => current.filter((item) => item.id !== id));
    } catch (error) {
      setShareKeysStatus(error instanceof Error ? error.message : '分享 Key 删除失败。');
    } finally {
      setDeletingShareKeyId(null);
    }
  }

  if (!session) {
    return (
      <main className="usage-page" id="top" aria-labelledby="usage-title">
        <section className="section usage-section">
          <article className="usage-empty-panel">
            <span className="usage-empty-icon" aria-hidden="true">
              <Wallet size={22} strokeWidth={2.2} />
            </span>
            <div>
              <h1 id="usage-title">余额和用量</h1>
              <p>登录后查看账户余额、冻结金额和模型调用消耗。</p>
            </div>
            <a className="primary-button" href="/login?returnTo=%2Fusage">
              <LogIn size={17} strokeWidth={2.2} />
              <span>登录</span>
            </a>
          </article>
        </section>
      </main>
    );
  }

  return (
    <main className="usage-page" id="top" aria-labelledby="usage-title">
      <section className="section usage-section" aria-busy={isUsageLoading}>
        <div className="usage-heading">
          <div>
            <h1 id="usage-title">余额和用量</h1>
            <p>{session.email || 'Vibeco 账号'}</p>
          </div>
          {isUsageLoading || usageStatus ? (
            <span>
              {isUsageLoading ? (
              <>
                <LoaderCircle size={14} strokeWidth={2.2} />
                Loading
              </>
              ) : (
                usageStatus
              )}
            </span>
          ) : null}
        </div>

        {isUsageLoading ? (
          <div className="usage-loading">
            <span className="loading-state" role="status" aria-label="Loading">
              <LoaderCircle size={19} strokeWidth={2.2} />
              <span>Loading</span>
            </span>
          </div>
        ) : (
          <>
            <div className="credits-layout">
              <section className="credits-balance-hero" aria-label="可用余额">
                <strong>{formatMilliCents(wallet.availableMilliCents)}</strong>
                <div className="credits-actions">
                  <button
                    className="secondary-button recharge-button share-balance-button"
                    type="button"
                    onClick={openShareKeyDialog}
                    data-tooltip="可以通过 API 和 Key 在其他工具中继续使用余额，例如 OpenCode、Cherry Studio 等"
                  >
                    分享
                  </button>
                  <button className="primary-button recharge-button" type="button" onClick={openRechargeDialog}>
                    充值
                  </button>
                </div>
                <button className="credits-info-button" type="button" aria-label="余额说明" title="当前可用余额">
                  <Info size={18} strokeWidth={2.2} />
                </button>
              </section>
            </div>

            {shareKeys.length > 0 ? (
              <ShareKeysPanel
                deletingId={deletingShareKeyId}
                isLoading={isShareKeysLoading}
                items={shareKeys}
                onDelete={deleteExistingShareKey}
                status={shareKeysStatus}
              />
            ) : null}

            <div className="usage-filter-row" aria-label="用量筛选">
              <label>
                <span>来自：</span>
                <input type="date" value={fromDate} onChange={(event) => updateFromDate(event.target.value)} />
              </label>
              <label>
                <span>至：</span>
                <input type="date" value={toDate} onChange={(event) => updateToDate(event.target.value)} />
              </label>
              <button className="secondary-button usage-filter-button" type="button" onClick={clearUsageFilters}>
                清除全部
              </button>
            </div>

            <article className="usage-chart-panel" aria-labelledby="usage-summary-title">
              <div className="usage-chart-tabs" role="tablist" aria-label="用量图表模式">
                <button
                  className={chartMode === 'cost' ? 'active' : ''}
                  type="button"
                  role="tab"
                  aria-selected={chartMode === 'cost'}
                  onClick={() => setChartMode('cost')}
                >
                  花费
                </button>
                <button
                  className={chartMode === 'requests' ? 'active' : ''}
                  type="button"
                  role="tab"
                  aria-selected={chartMode === 'requests'}
                  onClick={() => setChartMode('requests')}
                >
                  请求
                </button>
              </div>
              <div className="usage-chart-head">
                <div>
                  <h2 id="usage-summary-title">{chartMode === 'cost' ? '成本' : '请求'}</h2>
                  <p>{formatMilliCents(totals.costMilliCents)} · {formatInteger(totals.requestCount)} 次请求</p>
                </div>
              </div>
              <div className="usage-chart" aria-label={chartMode === 'cost' ? '每日花费柱状图' : '每日请求柱状图'}>
                {chartBuckets.map((bucket) => {
                  const value = chartMode === 'cost' ? bucket.costMilliCents : bucket.requestCount;
                  const height = value > 0 ? Math.max(4, (value / chartMaxValue) * 100) : 0;
                  return (
                    <div className="usage-chart-column" key={bucket.bucket}>
                      <div className="usage-chart-track">
                        {value > 0 ? (
                          <span className="usage-chart-bar" style={{ height: `${height}%` }}>
                            <span className="usage-chart-tooltip">
                              {formatChartDate(bucket.bucket)} · {chartMode === 'cost' ? formatMilliCents(bucket.costMilliCents) : `${formatInteger(bucket.requestCount)} 次请求`}
                            </span>
                          </span>
                        ) : null}
                      </div>
                      <span className="usage-chart-label">{formatChartDate(bucket.bucket)}</span>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="usage-panel" aria-labelledby="usage-activity-title">
              <div className="usage-panel-head">
                <div>
                  <h2 id="usage-activity-title">使用明细</h2>
                  <p>{fromDate} 至 {toDate} · 第 {activityPageIndex + 1} 页</p>
                </div>
                {isActivityLoading ? (
                  <span className="usage-panel-loading">
                    <LoaderCircle size={14} strokeWidth={2.2} />
                    Loading
                  </span>
                ) : null}
              </div>
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>时间</th>
                      <th>模型</th>
                      <th>成本</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityPage.items.length > 0 ? (
                      activityPage.items.map((item) => {
                        const timestamp = formatActivityTimestamp(item.createdAt);
                        return (
                          <tr key={item.id}>
                            <td>
                              <span className="usage-time-cell">
                                <strong>{timestamp.date}</strong>
                                <small>{timestamp.time}</small>
                              </span>
                            </td>
                            <td>{item.model}</td>
                            <td className="usage-cost-cell">
                              {formatCompactMilliCents(item.costMilliCents)} <span>({formatInteger(item.totalTokens)} tokens)</span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="usage-empty-row" colSpan={3}>{isActivityLoading ? 'Loading' : '当前周期暂无使用记录'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="usage-pagination" aria-label="使用明细分页">
                <span>第 {activityPageIndex + 1} 页</span>
                <div>
                  <button className="secondary-button" type="button" onClick={goToPreviousActivityPage} disabled={activityCursorStack.length === 0 || isActivityLoading}>
                    上一页
                  </button>
                  <button className="secondary-button" type="button" onClick={goToNextActivityPage} disabled={!activityPage.hasMore || !activityPage.nextCursor || isActivityLoading}>
                    下一页
                  </button>
                </div>
              </div>
            </article>

            <RechargeRecordsPanel
              canGoPrevious={rechargeCursorStack.length > 0}
              isLoading={isRechargeRecordsLoading}
              onNext={goToNextRechargePage}
              onPrevious={goToPreviousRechargePage}
              onRefresh={refreshRechargeRecords}
              page={rechargePage}
              pageIndex={rechargePageIndex}
              status={rechargeRecordsStatus}
            />

            {isShareKeyDialogOpen ? (
              <ShareKeyDialog
                error={shareKeyDialogError}
                form={shareKeyForm}
                isSubmitting={isGeneratingShareKey}
                onCancel={() => setIsShareKeyDialogOpen(false)}
                onChange={setShareKeyForm}
                onSubmit={submitShareKey}
              />
            ) : null}

            {isRechargeDialogOpen ? (
              <RechargeDialog
                amount={rechargeAmount}
                error={rechargeDialogError}
                isPaymentChecking={isPaymentChecking}
                isRateLoading={isRechargeRateLoading}
                isSubmitting={isRechargeSubmitting}
                onAmountChange={setRechargeAmount}
                onCancel={closeRechargeDialog}
                onCheckNow={checkPendingPaymentNow}
                onConfirm={submitRechargePayment}
                onReopenPayment={reopenPendingPayment}
                pendingPayment={pendingPayment}
                rate={rechargeRate}
              />
            ) : null}
          </>
        )}
      </section>
    </main>
  );
}

type AuthPageProps = {
  onAuthenticated: (session: AuthSession) => void;
  returnTo: string;
};

function AuthPage({ onAuthenticated, returnTo }: AuthPageProps) {
  const [authForm, setAuthForm] = useState<AuthFormState>({ confirmPassword: '', email: '', password: '' });
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [authState, setAuthState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [authMessage, setAuthMessage] = useState('请登录后继续。');
  const isAuthBusy = authState === 'loading';
  const authPanelTitle = authMode === 'register' ? '创建 Vibeco 账号' : '登录 Vibeco 账号';

  async function submitAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAuthState('loading');
    setAuthMessage(authMode === 'register' ? '正在注册...' : '正在登录...');

    try {
      const session = await authRequest(authMode, authForm);
      writeStoredAuthSession(session);
      onAuthenticated(session);
      setAuthForm((current) => ({ ...current, password: '', confirmPassword: '' }));
      setAuthState('idle');
      setAuthMessage(authMode === 'register' ? '注册成功' : '登录成功');
      window.setTimeout(() => window.location.replace(returnTo), 220);
    } catch (error) {
      setAuthState('error');
      setAuthMessage(error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <main className="auth-page" id="top" aria-labelledby="auth-title">
      <section className="auth-card">
        <div className="auth-card-header">
          <p className="eyebrow">Account</p>
          <h1 id="auth-title">{authPanelTitle}</h1>
        </div>

        <form className="auth-form" onSubmit={submitAuth}>
          <div className="auth-mode-tabs" role="tablist" aria-label="账号模式">
            <button className={authMode === 'login' ? 'active' : ''} type="button" onClick={() => setAuthMode('login')} role="tab" aria-selected={authMode === 'login'}>
              登录
            </button>
            <button className={authMode === 'register' ? 'active' : ''} type="button" onClick={() => setAuthMode('register')} role="tab" aria-selected={authMode === 'register'}>
              注册
            </button>
          </div>

          <label>
            <span>邮箱</span>
            <input
              autoComplete="email"
              onChange={(event) => setAuthForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="you@example.com"
              type="email"
              value={authForm.email}
            />
          </label>

          <label>
            <span>密码</span>
            <input
              autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
              onChange={(event) => setAuthForm((current) => ({ ...current, password: event.target.value }))}
              type="password"
              value={authForm.password}
            />
          </label>

          {authMode === 'register' ? (
            <label>
              <span>确认密码</span>
              <input
                autoComplete="new-password"
                onChange={(event) => setAuthForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                type="password"
                value={authForm.confirmPassword}
              />
            </label>
          ) : null}

          <button className="primary-button wide" type="submit" disabled={isAuthBusy}>
            {authMode === 'register' ? <UserPlus size={17} strokeWidth={2.2} /> : <LogIn size={17} strokeWidth={2.2} />}
            <span>{isAuthBusy ? '处理中' : authMode === 'register' ? '注册并进入' : '登录并进入'}</span>
          </button>

          <small className={authState === 'error' ? 'error' : ''}>{authMessage}</small>
        </form>
      </section>
    </main>
  );
}

function App() {
  const usesLiveModels = Boolean(appConfig.apiBaseUrl) && !appConfig.enableMocks;
  const [authSession, setAuthSession] = useState<AuthSession | null>(() => readStoredAuthSession());
  const [email, setEmail] = useState('');
  const [leadStatus, setLeadStatus] = useState('');
  const [downloadStatus, setDownloadStatus] = useState('');
  const [models, setModels] = useState<ModelRow[]>(usesLiveModels ? [] : fallbackModels);
  const [modelsStatus, setModelsStatus] = useState(usesLiveModels ? 'Loading' : '使用本地示例数据。');
  const [isModelsLoading, setIsModelsLoading] = useState(usesLiveModels);

  const docsHref = '/docs';
  const modelsHref = '/models';
  const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
  const isDocsPage = currentPath === '/docs';
  const isLoginPage = currentPath === '/login';
  const isModelsPage = currentPath === '/models';
  const isUsagePage = currentPath === '/usage';
  const returnTo = returnToFromLocation();

  useEffect(() => {
    if (!isModelsPage) return;

    const endpoint = apiUrl('/v1/models');
    if (!endpoint || appConfig.enableMocks) {
      setModels(fallbackModels);
      setModelsStatus('使用本地示例数据。');
      setIsModelsLoading(false);
      return;
    }

    const controller = new AbortController();
    setModels([]);
    setModelsStatus('Loading');
    setIsModelsLoading(true);

    fetch(endpoint, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`模型加载失败：${response.status}`);
        }
        return response.json();
      })
      .then((payload: unknown) => {
        const normalizedModels = normalizeModels(payload);
        if (normalizedModels.length === 0) {
          setModelsStatus('暂无可展示模型。');
          setModels([]);
          setIsModelsLoading(false);
          return;
        }
        setModels(normalizedModels);
        setModelsStatus(`已加载 ${normalizedModels.length} 个模型。`);
        setIsModelsLoading(false);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setModels([]);
        setModelsStatus(error instanceof Error ? error.message : '模型加载失败。');
        setIsModelsLoading(false);
      });

    return () => controller.abort();
  }, [isModelsPage]);

  function recordDownloadIntent() {
    const endpoint = apiUrl('/downloads/events');
    if (!endpoint || appConfig.enableMocks) return;

    fetch(endpoint, {
      body: JSON.stringify({ channel: appConfig.releaseChannel, source: 'frontend' }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    }).catch(() => undefined);
  }

  function handleDownload() {
    recordDownloadIntent();

    if (appConfig.downloadUrl) {
      window.location.assign(appConfig.downloadUrl);
      return;
    }

    setDownloadStatus('下载地址尚未配置。');
  }

  async function handleLogout() {
    const session = authSession;
    setAuthSession(null);
    clearStoredAuthSession();
    if (!session) return;

    try {
      await logoutRequest(session);
    } catch {
      // Keep the local logout behavior aligned with Electron when the token is
      // already invalid or the network is unavailable.
    }
  }

  async function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setLeadStatus('请输入有效邮箱。');
      return;
    }

    const endpoint = apiUrl('/leads');
    if (!endpoint || appConfig.enableMocks) {
      window.localStorage.setItem('vibeco.frontend.lead', normalizedEmail);
      setLeadStatus('已记录邮箱，接入 FC 后会提交到 /leads。');
      setEmail('');
      return;
    }

    try {
      const response = await fetch(endpoint, {
        body: JSON.stringify({ email: normalizedEmail, source: 'frontend', channel: appConfig.releaseChannel }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`提交失败：${response.status}`);
      }

      setLeadStatus('已收到，我们会发送下载和更新通知。');
      setEmail('');
    } catch (error) {
      setLeadStatus(error instanceof Error ? error.message : '提交失败，请稍后重试。');
    }
  }

  return (
    <>
      <SiteHeader
        isDocsPage={isDocsPage}
        isLoginPage={isLoginPage}
        isModelsPage={isModelsPage}
        isUsagePage={isUsagePage}
        onLogout={handleLogout}
        session={authSession}
      />

      {isDocsPage ? (
        <DocsPage />
      ) : isModelsPage ? (
        <ModelsPage isModelsLoading={isModelsLoading} models={models} modelsStatus={modelsStatus} />
      ) : isUsagePage ? (
        <UsagePage onSessionUpdated={setAuthSession} session={authSession} />
      ) : isLoginPage ? (
        <AuthPage onAuthenticated={setAuthSession} returnTo={returnTo} />
      ) : (
      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <img className="hero-image" src="/assets/vibeco-studio-hero.png" alt="" aria-hidden="true" />
          <div className="hero-overlay" aria-hidden="true" />

          <div className="hero-inner">
            <p className="eyebrow">Vibeco Studio</p>
            <h1 id="hero-title">Vibeco Studio</h1>
            <p className="hero-lead">从产品想法到可交付代码的 AI 工作台。生成设计、管理资产、推进源码，让灵感进入真实开发节奏。</p>

            <div className="hero-actions" aria-label="主要操作">
              <button className="primary-button" type="button" onClick={handleDownload}>
                <Download size={19} strokeWidth={2.2} />
                <span>下载 Vibeco Studio</span>
              </button>
            </div>

            <p className="runtime-note" aria-live="polite">
              {downloadStatus || `Release: ${appConfig.releaseChannel}`}
            </p>
          </div>

          <div className="hero-steps" aria-label="产品流程">
            {workflow.map((item) => (
              <span key={item.index}>
                <strong>{item.index}</strong>
                {item.title}
              </span>
            ))}
          </div>
        </section>

        <section className="section product-section" id="product" aria-labelledby="product-title">
          <div className="section-heading">
            <p className="eyebrow">Product</p>
            <h2 id="product-title">不是截图生成器，是面向交付的产品工作台</h2>
          </div>

          <div className="feature-grid">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <article className={`feature-card tone-${feature.tone}`} key={feature.title}>
                  <span className="feature-icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={2.2} />
                  </span>
                  <h3>{feature.title}</h3>
                  <p>{feature.copy}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="section workflow-section" id="workflow" aria-labelledby="workflow-title">
          <div className="section-heading">
            <p className="eyebrow">Workflow</p>
            <h2 id="workflow-title">把想法推进到可继续开发的项目</h2>
          </div>

          <div className="workflow-grid">
            {workflow.map((item) => (
              <article className="workflow-card" key={item.index}>
                <strong>{item.index}</strong>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="download-section" id="download" aria-labelledby="download-title">
          <div className="download-copy">
            <p className="eyebrow">Download</p>
            <h2 id="download-title">开始使用真实版本</h2>
            <div className="download-actions">
              <button className="primary-button" type="button" onClick={handleDownload}>
                <Download size={19} strokeWidth={2.2} />
                <span>下载桌面端</span>
              </button>
              <a className="secondary-button" href={docsHref}>
                <FileCode2 size={18} strokeWidth={2.2} />
                <span>查看文档</span>
              </a>
            </div>
          </div>

          <form className="lead-form" id="contact" onSubmit={submitLead}>
            <div>
              <Mail size={22} strokeWidth={2.2} />
              <h3>获取发布通知</h3>
            </div>
            <label>
              <span>邮箱</span>
              <input
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                type="email"
                value={email}
              />
            </label>
            <button className="primary-button wide" type="submit">
              <BadgeCheck size={18} strokeWidth={2.2} />
              <span>提交</span>
            </button>
            {leadStatus ? <p aria-live="polite">{leadStatus}</p> : null}
          </form>
        </section>
      </main>
      )}

      <SiteFooter docsHref={docsHref} modelsHref={modelsHref} />
    </>
  );
}

export default App;
